<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { AdminEditorTopBar, UiAsyncCombobox, UiButton, UiCheckbox, UiCombobox, UiIcon, UiIconButton, UiInputField, UiLink, UiLinkButton, UiNumberField, UiSelect, UiTag, UiText, UiTextarea, UiTooltip, countriesShopify, generateInitials, languagesEn, normalizeHttpHref, useApi, type ComboboxGroupData, type ComboboxItemData, type OverallReviewsSchema, type ShopexpertsPreviewExpert } from "@shopexperts/shared";
import type z from "zod";

import PartnerDirectoryBulkActionBar from "@/app/components/partner-directory/PartnerDirectoryBulkActionBar.vue";
import PartnerDirectoryMappedFieldRow from "@/app/components/partner-directory/PartnerDirectoryMappedFieldRow.vue";
import PartnerDirectoryIndexFilters from "@/app/components/partner-directory/PartnerDirectoryIndexFilters.vue";
import PartnerDirectoryRecordAvatar from "@/app/components/partner-directory/PartnerDirectoryRecordAvatar.vue";
import PartnerDirectoryRecordListCard from "@/app/components/partner-directory/PartnerDirectoryRecordListCard.vue";
import PartnerDirectoryRecordPreview from "@/app/components/partner-directory/PartnerDirectoryRecordPreview.vue";
import { useExpertSearch } from "@/app/composables/badges/useExpertSearch";
import { useAdminExpertDetail } from "@/app/composables/expert-manager/useAdminExpertDetail";
import { mapRecordToCrawlerProfilePayload } from "@/app/services/partnerDirectoryCrawlerProfile.mapper";
import { formatRecordListPosition } from "@/app/services/partnerDirectoryListPosition.util";
import { resolveShiftRange } from "@/app/services/partnerDirectoryRangeSelect.util";
import { countMappedOverrideFields, diffMappedPayload } from "@/app/services/partnerDirectoryMappedOverrides.util";
import { partnerDirectoryAvatarUrl } from "@/app/services/partnerDirectoryRecordAvatar";
import {
  PARTNER_DIRECTORY_DEFAULT_LIST_LIMIT,
  PARTNER_DIRECTORY_DEFAULT_MIN_RATING,
  PARTNER_DIRECTORY_DEFAULT_MIN_REVIEWS,
  PARTNER_DIRECTORY_DEFAULT_RECORD_SORT,
  PARTNER_DIRECTORY_DEFAULT_RECORD_STATUS_FILTER,
  PARTNER_DIRECTORY_INDEX_SIDEBAR_DEFAULT_WIDTH,
  PARTNER_DIRECTORY_INDEX_SIDEBAR_MAX_WIDTH,
  PARTNER_DIRECTORY_INDEX_SIDEBAR_MIN_WIDTH,
  usePartnerDirectoryFiltersStore,
} from "@/app/stores";

import {
  bulkRescrapePartnerDirectoryRecords,
  bulkSyncPartnerDirectoryRecords,
  bulkUpdatePartnerDirectoryRecordStatus,
  cancelPartnerDirectoryCrawlRun,
  checkPartnerDirectoryApiHealth,
  createPartnerDirectoryCrawlRun,
  createPartnerDirectoryScrapeTask,
  enqueuePartnerDirectoryCrawlScrapeTasks,
  createPartnerDirectoryRecordNote,
  deletePartnerDirectoryGlobalMapping,
  deletePartnerDirectoryRecordNote,
  getPartnerDirectoryApiConfig,
  getPartnerDirectoryCrawlRun,
  getPartnerDirectoryRecord,
  getPartnerDirectoryScrapeTask,
  listPartnerDirectoryCrawlPages,
  listPartnerDirectoryCrawlRuns,
  listPartnerDirectoryDiscoveredProfiles,
  listPartnerDirectoryGlobalMappings,
  listPartnerDirectoryRecordCountries,
  listPartnerDirectoryRecordNotes,
  listPartnerDirectoryRecords,
  listPartnerDirectoryScrapeTasks,
  listPartnerDirectoryServiceSourceValues,
  pausePartnerDirectoryCrawlRun,
  recoverPartnerDirectoryScrapeTasks,
  repairPartnerDirectoryReviewCountMismatches,
  rescrapePartnerDirectoryRecord,
  resetPartnerDirectoryCrawlRun,
  resumePartnerDirectoryCrawlRun,
  retryFailedPartnerDirectoryCrawlPages,
  retryFailedPartnerDirectoryScrapes,
  createPartnerDirectoryImportJob,
  getPartnerDirectoryImportJob,
  listPartnerDirectoryImportJobs,
  cancelPartnerDirectoryImportJob,
  listPartnerDirectoryImportJobRecords,
  syncPartnerDirectoryRecord,
  revertPartnerDirectoryImportJob,
  revertPartnerDirectoryRecordImport,
  updatePartnerDirectoryRecordMappedPayload,
  updatePartnerDirectoryRecordStatus,
  upsertPartnerDirectoryGlobalMapping,
  type PartnerDirectoryBulkRecordStatus,
  type PartnerDirectoryCrawlPage,
  type PartnerDirectoryCrawlPageStatus,
  type PartnerDirectoryCrawlRun,
  type PartnerDirectoryApiConfig,
  type PartnerDirectoryDiscoveredProfile,
  type PartnerDirectoryDiscoveredProfileStatus,
  type PartnerDirectoryExpertProfileStatus,
  type PartnerDirectoryGlobalMapping,
  type PartnerDirectoryGlobalMappingKind,
  type PartnerDirectoryImportJob,
  type PartnerDirectoryImportJobRevertResult,
  type PartnerDirectoryImportJobRecord,
  type PartnerDirectoryImportJobRecordOutcome,
  type PartnerDirectoryMappedExpertPayload,
  type PartnerDirectoryRecord,
  type PartnerDirectoryRecordNote,
  type PartnerDirectoryScrapeTask,
  type PartnerDirectoryScrapeTaskStatus,
} from "@/app/services/partnerDirectoryApi.service";

type ScrapedReview = {
  author?: string | null;
  date?: string | null;
  rating?: number | null;
  ratings?: {
    qualityOfWork?: number | null;
    communication?: number | null;
  } | null;
  text?: string | null;
  serviceReviewed?: string | null;
};

type ScrapedPrice = {
  label?: string | null;
  amountCents?: number | null;
  currencyCode?: string | null;
  pricingType?: string | null;
};

type RatingDistributionItem = {
  stars?: number | null;
  count?: number | null;
};

type RatingDistributionEntry = {
  stars: number;
  count: number;
};

type ScrapedProfile = {
  name?: string | null;
  slug?: string | null;
  partnerType?: string | null;
  partnerSince?: string | null;
  tier?: string | null;
  logo?: {
    src?: string | null;
    alt?: string | null;
  } | null;
  contact?: {
    website?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  socialLinks?: {
    linkedin?: string | null;
    x?: string | null;
    facebook?: string | null;
    instagram?: string | null;
    youtube?: string | null;
    tiktok?: string | null;
  } | null;
  priceRange?: ScrapedPrice | null;
  ratingSummary?: {
    overallRating?: number | null;
    reviewCount?: number | null;
    label?: string | null;
    summaryLabel?: string | null;
    summaryText?: string | null;
    ratingBasis?: {
      qualityOfWork?: string | null;
      communication?: string | null;
    } | null;
    distribution?: RatingDistributionItem[] | null;
  } | null;
  about?: {
    businessDescription?: string | null;
    tierDescription?: string | null;
  } | null;
  primaryLocation?: string | null;
  supportedLocations?: unknown[] | null;
  languages?: unknown[] | null;
  otherServices?: unknown[] | null;
  specializedServices?: unknown[] | null;
  industries?: unknown[] | null;
  featuredWork?: unknown[] | null;
  reviews?: ScrapedReview[] | null;
};

type ScrapedPayload = {
  profile?: ScrapedProfile | null;
  scrapedAt?: string | null;
  page?: {
    title?: string | null;
    metaDescription?: string | null;
  } | null;
};

type SpecializedServiceView = {
  name: string;
  description: string | null;
  priceLabel: string | null;
  priceAmountCents: number | null;
};

type MappedExpertPayload = PartnerDirectoryMappedExpertPayload;

type SubcategoryOption = {
  label: string;
  value: string;
};

type SubcategoryDetails = {
  id: number;
  name: string;
  slug: string;
  category: {
    id: number;
    name: string;
    slug: string;
  };
};

type CategoryWithSubcategories = {
  id: number;
  name: string;
  slug: string;
  subcategories: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
};

type OverallReviews = z.infer<typeof OverallReviewsSchema>;

type ServiceSourceValue = {
  value: string;
  recordCount: number;
};

type CrawlPageFilter = "all" | PartnerDirectoryCrawlPageStatus;
type DiscoveredProfileFilter = "all" | PartnerDirectoryDiscoveredProfileStatus;
type ActiveTab = "crawler" | "index" | "import" | "mapping";

const DEFAULT_CRAWL_SEED_URL = "https://www.shopify.com/partners/directory/services";
const ACTIVE_POLL_INTERVAL_MS = 2500;
const IDLE_POLL_INTERVAL_MS = 8000;
const FALLBACK_MAX_LIST_LIMIT = 1000;
const IMPORT_JOBS_PAGE_SIZE = 20;
const IMPORT_JOB_RECORDS_PAGE_SIZE = 25;
const PUBLIC_WEBSITE_URL = ((import.meta.env.VITE_WEBSITE_URL as string | undefined) ?? "").replace(/\/$/, "");
const importJobTargetStatusOptions = [
  { label: "Published", value: "published" },
  { label: "Draft", value: "draft" },
];
const SERVICE_MAPPING_KIND: PartnerDirectoryGlobalMappingKind = "service";
const EMPTY_MAPPED_PAYLOAD: MappedExpertPayload = {
  expert_id: null,
  expert_type: "agency",
  agency_name: null,
  first_name: null,
  last_name: null,
  bio: null,
  country: null,
  starting_price: null,
  timezone: null,
  phone_no: null,
  languages: [],
  social_links: {
    website: null,
    shopify_directory_link: null,
    linkedin_url: null,
    x_url: null,
  },
  subcategory_slugs: [],
};
const COMMON_COUNTRY_CODES: Record<string, string> = {
  "united states": "US",
  usa: "US",
  "united kingdom": "GB",
  uk: "GB",
  canada: "CA",
  australia: "AU",
  india: "IN",
  germany: "DE",
  france: "FR",
  spain: "ES",
  italy: "IT",
  netherlands: "NL",
  ireland: "IE",
  portugal: "PT",
  brazil: "BR",
  mexico: "MX",
  singapore: "SG",
  "new zealand": "NZ",
  "south africa": "ZA",
  philippines: "PH",
  pakistan: "PK",
  bangladesh: "BD",
  "united arab emirates": "AE",
};
const COUNTRY_CODES_BY_NAME = new Map([
  ...countriesShopify.map((country) => [normalizeLookupValue(country.name), country.iso2.toUpperCase()] as const),
  ...Object.entries(COMMON_COUNTRY_CODES).map(([name, code]) => [normalizeLookupValue(name), code] as const),
]);
const LANGUAGE_CODES_BY_NAME = new Map([
  ...languagesEn.map((language) => [normalizeLookupValue(language.name), language.code.toLowerCase()] as const),
  ["mandarin", "zh"],
  ["chinese", "zh"],
  ["cantonese", "yue"],
  ["portuguese", "pt"],
  ["brazilian portuguese", "pt"],
]);
const DEFAULT_TIMEZONE_BY_COUNTRY_CODE: Record<string, string> = {
  AE: "Asia/Dubai",
  AR: "America/Argentina/Buenos_Aires",
  AU: "Australia/Sydney",
  BD: "Asia/Dhaka",
  BE: "Europe/Brussels",
  BR: "America/Sao_Paulo",
  CA: "America/Toronto",
  CH: "Europe/Zurich",
  CL: "America/Santiago",
  CN: "Asia/Shanghai",
  CO: "America/Bogota",
  DE: "Europe/Berlin",
  DK: "Europe/Copenhagen",
  EG: "Africa/Cairo",
  ES: "Europe/Madrid",
  FR: "Europe/Paris",
  GB: "Europe/London",
  HK: "Asia/Hong_Kong",
  ID: "Asia/Jakarta",
  IE: "Europe/Dublin",
  IL: "Asia/Jerusalem",
  IN: "Asia/Kolkata",
  IT: "Europe/Rome",
  JP: "Asia/Tokyo",
  MX: "America/Mexico_City",
  MY: "Asia/Kuala_Lumpur",
  NG: "Africa/Lagos",
  NL: "Europe/Amsterdam",
  NO: "Europe/Oslo",
  NZ: "Pacific/Auckland",
  PH: "Asia/Manila",
  PK: "Asia/Karachi",
  PL: "Europe/Warsaw",
  PT: "Europe/Lisbon",
  SE: "Europe/Stockholm",
  SG: "Asia/Singapore",
  TH: "Asia/Bangkok",
  TR: "Europe/Istanbul",
  UA: "Europe/Kyiv",
  US: "America/New_York",
  VN: "Asia/Ho_Chi_Minh",
  ZA: "Africa/Johannesburg",
};
const US_TIMEZONE_BY_STATE: Record<string, string> = {
  AK: "America/Anchorage",
  AL: "America/Chicago",
  AR: "America/Chicago",
  AZ: "America/Phoenix",
  CA: "America/Los_Angeles",
  CO: "America/Denver",
  CT: "America/New_York",
  DC: "America/New_York",
  DE: "America/New_York",
  FL: "America/New_York",
  GA: "America/New_York",
  HI: "Pacific/Honolulu",
  IA: "America/Chicago",
  ID: "America/Boise",
  IL: "America/Chicago",
  IN: "America/Indiana/Indianapolis",
  KS: "America/Chicago",
  KY: "America/New_York",
  LA: "America/Chicago",
  MA: "America/New_York",
  MD: "America/New_York",
  ME: "America/New_York",
  MI: "America/Detroit",
  MN: "America/Chicago",
  MO: "America/Chicago",
  MS: "America/Chicago",
  MT: "America/Denver",
  NC: "America/New_York",
  ND: "America/Chicago",
  NE: "America/Chicago",
  NH: "America/New_York",
  NJ: "America/New_York",
  NM: "America/Denver",
  NV: "America/Los_Angeles",
  NY: "America/New_York",
  OH: "America/New_York",
  OK: "America/Chicago",
  OR: "America/Los_Angeles",
  PA: "America/New_York",
  RI: "America/New_York",
  SC: "America/New_York",
  SD: "America/Chicago",
  TN: "America/Chicago",
  TX: "America/Chicago",
  UT: "America/Denver",
  VA: "America/New_York",
  VT: "America/New_York",
  WA: "America/Los_Angeles",
  WI: "America/Chicago",
  WV: "America/New_York",
  WY: "America/Denver",
};
const CANADA_TIMEZONE_BY_PROVINCE: Record<string, string> = {
  AB: "America/Edmonton",
  BC: "America/Vancouver",
  MB: "America/Winnipeg",
  NB: "America/Moncton",
  NL: "America/St_Johns",
  NS: "America/Halifax",
  NT: "America/Yellowknife",
  NU: "America/Iqaluit",
  ON: "America/Toronto",
  PE: "America/Halifax",
  QC: "America/Toronto",
  SK: "America/Regina",
  YT: "America/Whitehorse",
};

const api = useApi();
const partnerDirectoryFiltersStore = usePartnerDirectoryFiltersStore();
const {
  hasStoredCountryFilter,
  importedRecordsLimit,
  indexSidebarWidth,
  recordCountryFilter,
  recordMinRating,
  recordMinReviews,
  recordSort,
  recordStatusFilter,
} = storeToRefs(partnerDirectoryFiltersStore);
const input = ref("");
const force = ref(false);
const activeTab = ref<ActiveTab>("index");
const expertsPanelOpen = ref(true);
const isSubmitting = ref(false);
const isStartingCrawl = ref(false);
const isRunningCrawlAction = ref(false);
const isUpdatingRecordStatus = ref(false);
const isRecoveringQueue = ref(false);
const queueRecoveryMessage = ref<string | null>(null);
const isRepairingReviewCounts = ref(false);
const reviewRepairMessage = ref<string | null>(null);
const errorMessage = ref<string | null>(null);
const crawlerMessage = ref<string | null>(null);
const apiHealthStatus = ref<"checking" | "online" | "offline">("checking");
const apiConfig = ref<PartnerDirectoryApiConfig | null>(null);
const activeTask = ref<PartnerDirectoryScrapeTask | null>(null);
const recentTasks = ref<PartnerDirectoryScrapeTask[]>([]);
const crawlRuns = ref<PartnerDirectoryCrawlRun[]>([]);
const selectedCrawlRunId = ref<string | null>(null);
const selectedCrawlRun = ref<PartnerDirectoryCrawlRun | null>(null);
const crawlPages = ref<PartnerDirectoryCrawlPage[]>([]);
const discoveredProfiles = ref<PartnerDirectoryDiscoveredProfile[]>([]);
const selectedDiscoveredProfileIds = ref<string[]>([]);
const failedCrawlPages = ref<PartnerDirectoryCrawlPage[]>([]);
const failedDiscoveredProfiles = ref<PartnerDirectoryDiscoveredProfile[]>([]);
const importedRecords = ref<PartnerDirectoryRecord[]>([]);
const importedRecordsTotal = ref(0);
const allImportedRecordsTotal = ref(0);
const recordCountryValues = ref<string[]>([]);
const hasUnknownRecordCountry = ref(false);
const importedRecordsPage = ref(1);
const hoveredRecordIndex = ref<number | null>(null);
const isLoadingImportedRecords = ref(false);
let importedRecordsRefreshQueued = false;
let recordSearchRefreshTimer: number | null = null;
const selectedRecordId = ref<string | null>(null);
const selectedRecord = ref<PartnerDirectoryRecord | null>(null);
const resultRecord = ref<PartnerDirectoryRecord | null>(null);
const recordSearch = ref("");
const crawlSeedUrl = ref("");
const crawlMaxPages = ref("");
const crawlMaxProfiles = ref("");
const crawlAutoQueueLimit = ref("");
const manualQueueLimit = ref("");
const recentTasksLimit = ref("10");
const crawlRunsLimit = ref("25");
const crawlPagesLimit = ref("100");
const discoveredProfilesLimit = ref("100");
const crawlQueueScrapeTasks = ref(false);
const crawlForceScrapeRefresh = ref(false);
const crawlPageStatusFilter = ref<CrawlPageFilter>("all");
const discoveredProfileStatusFilter = ref<DiscoveredProfileFilter>("all");
const expertViewerTab = ref<"screener" | "notes" | "preview">("screener");
const pollTimer = ref<number | null>(null);
const isPageMounted = ref(false);
const selectedRecordLoadToken = ref(0);
const shouldAutoSelectResultRecord = ref(false);
const importJobs = ref<PartnerDirectoryImportJob[]>([]);
const importJobsTotal = ref(0);
const importJobsPage = ref(1);
const isStartingImportJob = ref(false);
const isCancellingImportJob = ref(false);
const newImportJobTargetStatus = ref<PartnerDirectoryExpertProfileStatus>("published");
const selectedImportJobId = ref<string | null>(null);
// Fallback when paging the history drops the selected job from both lists;
// its targetProfileStatus never changes, so a stale snapshot is still correct for retries.
const selectedImportJobSnapshot = ref<PartnerDirectoryImportJob | null>(null);
const selectedImportJobRecords = ref<PartnerDirectoryImportJobRecord[]>([]);
const selectedImportJobRecordsTotal = ref(0);
const selectedImportJobRecordsPage = ref(1);
const selectedImportJobRecordsLoadToken = ref(0);
const selectedImportJobRecordsOutcome = ref<PartnerDirectoryImportJobRecordOutcome | "all">("all");
const importJobsLoadToken = ref(0);
let activeImportJobPollToken = 0;
const isLoadingImportJobRecords = ref(false);
const revertingRecordId = ref<string | null>(null);
const syncingRecordId = ref<string | null>(null);
const revertingJobId = ref<string | null>(null);
const importJobRevertMessage = ref<string | null>(null);
// Revert failures (e.g. the pre-registry 409) surface inline in the Import tab.
const importJobRevertError = ref<string | null>(null);
const isRescrapingRecord = ref(false);
const recordActionMessage = ref<string | null>(null);
// Server-derived og baseline for the selected record's mapper (§ derivedDefaults).
const selectedRecordDerivedDefaults = ref<MappedExpertPayload | null>(null);
// True when the detail fetch failed and the mapper runs on the client-side
// prefill baseline, whose derivation can diverge from the server's.
const selectedRecordDetailFetchFailed = ref(false);
// Set when a re-scrape finished for the open record while the mapper draft was
// dirty: the og baseline changed underneath unsaved edits.
const rescrapedBaselineStale = ref(false);
// Re-baselining after a re-scrape must be edge-triggered: polling keeps the
// finished task around, so remember which task we already handled.
let lastBaselineRefreshTaskId: string | null = null;
const selectedRecordIds = ref<Set<string>>(new Set());
const isRunningBulkAction = ref(false);
const bulkActionMessage = ref<string | null>(null);
const bulkSyncTargetStatus = ref<PartnerDirectoryExpertProfileStatus>("published");
const isResizingIndexSidebar = ref(false);
let indexSidebarDragStartX = 0;
let indexSidebarDragStartWidth = PARTNER_DIRECTORY_INDEX_SIDEBAR_DEFAULT_WIDTH;

const canSubmit = computed(() => input.value.trim().length > 0 && !isSubmitting.value);
const isActiveTaskPending = computed(() => (activeTask.value ? isScrapeTaskPending(activeTask.value) : false));
const hasPendingRecentTasks = computed(() => recentTasks.value.some(isScrapeTaskPending));
const isSelectedCrawlRunActive = computed(() => selectedCrawlRun.value?.status === "queued" || selectedCrawlRun.value?.status === "running");
// Queued and running jobs, tracked independently of the paginated history so
// navigating pages can't stop polling them.
const activeImportJobs = ref<PartnerDirectoryImportJob[]>([]);
const importJobsPageCount = computed(() => Math.max(1, Math.ceil(importJobsTotal.value / IMPORT_JOBS_PAGE_SIZE)));
const canGoToPreviousImportJobsPage = computed(() => importJobsPage.value > 1);
const canGoToNextImportJobsPage = computed(() => importJobsPage.value < importJobsPageCount.value);
const selectedImportJob = computed(() => {
  const id = selectedImportJobId.value;
  return importJobs.value.find((job) => job.id === id) ?? activeImportJobs.value.find((job) => job.id === id) ?? selectedImportJobSnapshot.value;
});
const selectedImportJobOutcomeOptions = computed(() => {
  const job = selectedImportJob.value;
  if (!job) return [{ label: "All outcomes", value: "all" }];
  return [
    { label: `All outcomes (${job.recordsSucceeded + job.recordsFailed + job.recordsSkipped})`, value: "all" },
    { label: `Failed (${job.recordsFailed})`, value: "failed" },
    { label: `Skipped (${job.recordsSkipped})`, value: "skipped" },
    { label: `Imported (${job.recordsSucceeded})`, value: "imported" },
  ];
});
const selectedImportJobRecordsPageCount = computed(() => Math.max(1, Math.ceil(selectedImportJobRecordsTotal.value / IMPORT_JOB_RECORDS_PAGE_SIZE)));
const canGoToPreviousImportJobRecordsPage = computed(() => selectedImportJobRecordsPage.value > 1 && !isLoadingImportJobRecords.value);
const canGoToNextImportJobRecordsPage = computed(() => selectedImportJobRecordsPage.value < selectedImportJobRecordsPageCount.value && !isLoadingImportJobRecords.value);
const shouldPollOperations = computed(() => isActiveTaskPending.value || hasPendingRecentTasks.value || isSelectedCrawlRunActive.value || activeImportJobs.value.length > 0);
const canStartCrawl = computed(() => !isStartingCrawl.value);
const crawlerRunsCount = computed(() => crawlRuns.value.length);
const selectedCrawlRunPagesCount = computed(() => crawlPages.value.length);
const selectedCrawlRunProfilesCount = computed(() => discoveredProfiles.value.length);
const selectedRunCanPause = computed(() => selectedCrawlRun.value?.status === "queued" || selectedCrawlRun.value?.status === "running");
const selectedRunCanResume = computed(() => selectedCrawlRun.value?.status === "paused" || selectedCrawlRun.value?.status === "failed");
const selectedRunCanCancel = computed(() => selectedCrawlRun.value?.status === "queued" || selectedCrawlRun.value?.status === "running" || selectedCrawlRun.value?.status === "paused");
const selectedRunHasDiscoveredProfiles = computed(() => discoveredProfiles.value.some((profile) => profile.status === "discovered"));
const queueScrapesTooltip = computed(() =>
  selectedRunHasDiscoveredProfiles.value
    ? 'Queues scrape tasks for profiles in "discovered" state, up to the queue limit.'
    : 'No profiles left in "discovered" state. Tick "Force re-scrape indexed" and use Queue selected to re-scrape indexed profiles.',
);
const selectedRunHasFailedPages = computed(() => failedCrawlPages.value.length > 0 || crawlPages.value.some((page) => page.status === "failed"));
const selectedRunHasFailedScrapes = computed(
  () => failedDiscoveredProfiles.value.length > 0 || discoveredProfiles.value.some((profile) => profile.status === "scrape_failed"),
);

const crawlPageStatusOptions = [
  { label: "All crawl URLs", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Running", value: "running" },
  { label: "Succeeded", value: "succeeded" },
  { label: "Failed", value: "failed" },
  { label: "Skipped", value: "skipped" },
];

const discoveredProfileStatusOptions = [
  { label: "All profiles", value: "all" },
  { label: "Discovered", value: "discovered" },
  { label: "Already indexed", value: "already_indexed" },
  { label: "Scrape queued", value: "scrape_queued" },
  { label: "Scraped", value: "scraped" },
  { label: "Scrape failed", value: "scrape_failed" },
  { label: "Skipped", value: "skipped" },
];

const recordStatusFilterOptions = [
  { label: "To screen (no rejected)", value: "screening" },
  { label: "New", value: "new" },
  { label: "Needs review", value: "needs_review" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Imported", value: "imported" },
  { label: "All statuses", value: "all" },
];

const recordMinRatingOptions = [
  { label: "Any rating", value: "0" },
  { label: "4.5+ stars", value: "4.5" },
  { label: "4+ stars", value: "4" },
  { label: "3+ stars", value: "3" },
];

const recordMinReviewsOptions = [
  { label: "Any reviews", value: "0" },
  { label: "1+ reviews", value: "1" },
  { label: "10+ reviews", value: "10" },
  { label: "50+ reviews", value: "50" },
];

const recordSortOptions = [
  { label: "Newest first", value: "newest" },
  { label: "Recently edited", value: "recently_edited" },
  { label: "Highest score", value: "score" },
  { label: "Highest rating", value: "rating" },
  { label: "Most reviews", value: "reviews" },
];

const expertTypeOptions = [
  { label: "Agency", value: "agency" },
  { label: "Freelancer", value: "freelancer" },
];

const mappingKindOptions = [{ label: "Offered services", value: SERVICE_MAPPING_KIND }];
const mappingFilterOptions = [
  { label: "All", value: "all" },
  { label: "Unmapped only", value: "unmapped" },
  { label: "Mapped only", value: "mapped" },
];

const UNKNOWN_COUNTRY_FILTER_VALUE = "__unknown__";
const seenRecordCountryValues = new Set<string>();
let hasInitializedRecordCountryOptions = false;

const recordCountryFilterModel = computed<string[]>({
  get: () => recordCountryFilter.value,
  set: (countries) => {
    partnerDirectoryFiltersStore.setRecordCountryFilter(countries);
    resetAndRefreshImportedRecords();
  },
});

const recordCountryOptions = computed(() => {
  const options = recordCountryValues.value.map((country) => ({ label: country, value: country }));
  if (hasUnknownRecordCountry.value) options.push({ label: "Unknown country", value: UNKNOWN_COUNTRY_FILTER_VALUE });
  return options;
});

// Countries seen for the first time are auto-selected so the default stays "all countries",
// while manual deselections survive refreshes and limit changes.
watch(
  recordCountryOptions,
  (options) => {
    const newValues = options.map((option) => option.value).filter((value) => !seenRecordCountryValues.has(value));
    if (newValues.length === 0) return;
    newValues.forEach((value) => seenRecordCountryValues.add(value));

    if (!hasInitializedRecordCountryOptions) {
      hasInitializedRecordCountryOptions = true;
      if (hasStoredCountryFilter.value) {
        // Countries and records load concurrently, so reapply the restored
        // country constraint once its available options have initialized.
        resetAndRefreshImportedRecords();
        return;
      }
    }

    partnerDirectoryFiltersStore.setRecordCountryFilter([...recordCountryFilter.value, ...newValues]);
  },
  { immediate: true },
);

function selectAllRecordCountries() {
  recordCountryFilterModel.value = recordCountryOptions.value.map((option) => option.value);
}

function deselectAllRecordCountries() {
  recordCountryFilterModel.value = [];
}

// Back to the defaults the drawer counts against; the country default is "all
// available countries selected", which also refreshes the list.
function resetIndexFilters() {
  recordStatusFilter.value = PARTNER_DIRECTORY_DEFAULT_RECORD_STATUS_FILTER;
  recordMinRating.value = PARTNER_DIRECTORY_DEFAULT_MIN_RATING;
  recordMinReviews.value = PARTNER_DIRECTORY_DEFAULT_MIN_REVIEWS;
  recordSort.value = PARTNER_DIRECTORY_DEFAULT_RECORD_SORT;
  importedRecordsLimit.value = PARTNER_DIRECTORY_DEFAULT_LIST_LIMIT;
  selectAllRecordCountries();
}
const subcategoryOptions = ref<SubcategoryOption[]>([]);
const subcategoryOptionGroups = ref<ComboboxGroupData[]>([]);
const subcategoryDetailsBySlug = ref<Map<string, SubcategoryDetails>>(new Map());
const isSavingMappedPayload = ref(false);
const mappedPayloadDraft = ref<MappedExpertPayload | null>(null);
const mappedPayloadLastSaved = ref<MappedExpertPayload | null>(null);
const mappedSubcategoryToAdd = ref("");
const mappedExpertSearchQuery = ref("");
const mappedExpertSelectedItems = ref<ComboboxItemData[]>([]);
const notes = ref<PartnerDirectoryRecordNote[]>([]);
const noteDraft = ref("");
const isLoadingNotes = ref(false);
const isSavingNote = ref(false);
const deletingNoteIds = ref<string[]>([]);
const serviceSourceValues = ref<ServiceSourceValue[]>([]);
const serviceMappings = ref<PartnerDirectoryGlobalMapping[]>([]);
const isLoadingMappings = ref(false);
const hasLoadedMappings = ref(false);
const mappingSearch = ref("");
const mappingFilter = ref<"all" | "mapped" | "unmapped">("all");

// Search, filters, and sorting are applied by the API before pagination.
const filteredRecords = computed(() => importedRecords.value);

const selectedRecordIdCount = computed(() => selectedRecordIds.value.size);
const allPageRecordsSelected = computed(() => filteredRecords.value.length > 0 && filteredRecords.value.every((record) => selectedRecordIds.value.has(record.id)));

function toggleRecordChecked(recordId: string, index: number, rangeSelect = false) {
  if (rangeSelect) {
    extendRecordSelectionTo(index);
    return;
  }

  const next = new Set(selectedRecordIds.value);
  if (next.has(recordId)) {
    next.delete(recordId);
  } else {
    next.add(recordId);
  }
  selectedRecordIds.value = next;
}

function extendRecordSelectionTo(index: number) {
  const records = filteredRecords.value;
  const selectedIndices = records.reduce<number[]>((indices, record, position) => {
    if (selectedRecordIds.value.has(record.id)) indices.push(position);
    return indices;
  }, []);

  const range = resolveShiftRange({ index, selectedIndices, renderedCount: records.length });
  if (range.length === 0) return;

  const next = new Set(selectedRecordIds.value);
  range.forEach((position) => {
    const record = records[position];
    if (record) next.add(record.id);
  });
  selectedRecordIds.value = next;
}

function toggleSelectAllOnPage() {
  const next = new Set(selectedRecordIds.value);
  if (allPageRecordsSelected.value) {
    filteredRecords.value.forEach((record) => next.delete(record.id));
  } else {
    filteredRecords.value.forEach((record) => next.add(record.id));
  }
  selectedRecordIds.value = next;
}

function clearRecordSelection() {
  if (selectedRecordIds.value.size) selectedRecordIds.value = new Set();
}

// After every list refresh, keep only rows that are still shown.
function pruneRecordSelection() {
  const presentIds = new Set(importedRecords.value.map((record) => record.id));
  selectedRecordIds.value = new Set([...selectedRecordIds.value].filter((id) => presentIds.has(id)));
}

// Index-tab sidebar resizing: drag the handle on the aside's right edge.
// null width = default w-80; the store clamps and persists the value.
const indexSidebarStyle = computed(() => (indexSidebarWidth.value !== null ? { width: `${indexSidebarWidth.value}px` } : undefined));

function startIndexSidebarResize(event: PointerEvent) {
  if (event.button !== 0) return;
  event.preventDefault();
  isResizingIndexSidebar.value = true;
  indexSidebarDragStartX = event.clientX;
  indexSidebarDragStartWidth = indexSidebarWidth.value ?? PARTNER_DIRECTORY_INDEX_SIDEBAR_DEFAULT_WIDTH;
  (event.currentTarget as HTMLElement | null)?.setPointerCapture(event.pointerId);
}

function onIndexSidebarResize(event: PointerEvent) {
  if (!isResizingIndexSidebar.value) return;
  partnerDirectoryFiltersStore.setIndexSidebarWidth(indexSidebarDragStartWidth + (event.clientX - indexSidebarDragStartX));
}

function endIndexSidebarResize(event: PointerEvent) {
  if (!isResizingIndexSidebar.value) return;
  isResizingIndexSidebar.value = false;
  (event.currentTarget as HTMLElement | null)?.releasePointerCapture(event.pointerId);
}

function resetIndexSidebarWidth() {
  partnerDirectoryFiltersStore.setIndexSidebarWidth(null);
}

function onIndexSidebarResizeKeydown(event: KeyboardEvent) {
  if (event.key === "Home") {
    event.preventDefault();
    resetIndexSidebarWidth();
    return;
  }

  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  const direction = event.key === "ArrowLeft" ? -1 : 1;
  partnerDirectoryFiltersStore.setIndexSidebarWidth((indexSidebarWidth.value ?? PARTNER_DIRECTORY_INDEX_SIDEBAR_DEFAULT_WIDTH) + direction * 16);
}

const selectedProfile = computed(() => (selectedRecord.value ? getProfile(selectedRecord.value) : null));

const selectedRecordServices = computed(() => {
  if (!selectedRecord.value) return [];

  return servicesFromRecord(selectedRecord.value);
});

const selectedSpecializedServices = computed<SpecializedServiceView[]>(() => {
  return toArray(selectedProfile.value?.specializedServices)
    .map((service) => {
      if (typeof service === "string") {
        return {
          name: service,
          description: null,
          priceLabel: null,
          priceAmountCents: null,
        };
      }

      if (!service || typeof service !== "object") return null;

      const serviceRecord = service as Record<string, unknown>;
      const name = valueToDisplayString(serviceRecord.name);
      if (!name) return null;
      const price = objectValue(serviceRecord.price);

      return {
        name,
        description: valueToDisplayString(serviceRecord.description),
        priceLabel: valueToDisplayString(price?.label),
        priceAmountCents: numberValue(price?.amountCents),
      };
    })
    .filter((service): service is SpecializedServiceView => Boolean(service));
});

const selectedOtherServices = computed(() => uniqueStrings(toArray(selectedProfile.value?.otherServices)));
const hasSelectedServices = computed(() => selectedSpecializedServices.value.length > 0 || selectedOtherServices.value.length > 0);
const selectedSupportedLocations = computed(() => uniqueStrings(toArray(selectedProfile.value?.supportedLocations)));
const selectedLanguages = computed(() => uniqueStrings(toArray(selectedProfile.value?.languages)));
const selectedIndustries = computed(() => uniqueStrings(toArray(selectedProfile.value?.industries)));

const selectedRecordReviews = computed(() => selectedProfile.value?.reviews ?? []);

const selectedPayload = computed(() => (selectedRecord.value?.rawPayload ?? {}) as ScrapedPayload);
const selectedRecordNoteCount = computed(() => selectedRecord.value?.noteCount ?? notes.value.length);
// sourceUrl is a crawler-supplied string with no scheme enforcement anywhere in
// the pipeline, so it only becomes an href once it survives normalization.
const selectedRecordSourceHref = computed(() => normalizeHttpHref(selectedRecord.value?.sourceUrl));
const mappedPayloadDirty = computed(() => JSON.stringify(mappedPayloadDraft.value) !== JSON.stringify(mappedPayloadLastSaved.value));
const mappedPayloadBaseline = computed<MappedExpertPayload | null>(() => {
  const record = selectedRecord.value;
  if (!record) return null;
  // Fall back to the client-side prefill until the server-derived defaults land.
  return selectedRecordDerivedDefaults.value ?? prefillMappedPayload(record);
});
const mappedOverrides = computed(() => {
  const draft = mappedPayloadDraft.value;
  const baseline = mappedPayloadBaseline.value;
  if (!draft || !baseline) return null;
  return diffMappedPayload(draft, baseline);
});
const overriddenMappedFields = computed<Set<string>>(() => {
  const overrides = mappedOverrides.value;
  const fields = new Set<string>();
  if (!overrides) return fields;
  for (const [key, value] of Object.entries(overrides)) {
    if (key === "social_links" && value && typeof value === "object") {
      Object.keys(value).forEach((socialKey) => fields.add(`social_links.${socialKey}`));
    } else {
      fields.add(key);
    }
  }
  return fields;
});
const mappedOverrideFieldCount = computed(() => countMappedOverrideFields(mappedOverrides.value));
const mappedOverrideFieldCountLabel = computed(() => (mappedOverrideFieldCount.value === 1 ? "1 field mapped" : `${mappedOverrideFieldCount.value} fields mapped`));

function isMappedFieldOverridden(field: string): boolean {
  return overriddenMappedFields.value.has(field);
}

function formatScrapedValue(value: unknown): string {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function resetMappedField<K extends keyof MappedExpertPayload>(field: K) {
  const draft = mappedPayloadDraft.value;
  const baseline = mappedPayloadBaseline.value;
  if (!draft || !baseline) return;
  draft[field] = cloneMappedPayload(baseline)[field];
}

function resetMappedSocialLinkField(field: keyof MappedExpertPayload["social_links"]) {
  const draft = mappedPayloadDraft.value;
  const baseline = mappedPayloadBaseline.value;
  if (!draft || !baseline) return;
  draft.social_links[field] = baseline.social_links[field];
}

function clearAllMappedOverrides() {
  const baseline = mappedPayloadBaseline.value;
  if (!baseline || !mappedPayloadDraft.value) return;
  setMappedExpertSelection(null);
  mappedPayloadDraft.value = cloneMappedPayload(baseline);
}
const mappedExpertId = computed<string | undefined>({
  get: () => {
    const expertId = mappedPayloadDraft.value?.expert_id;
    return typeof expertId === "number" ? String(expertId) : undefined;
  },
  set: (value) => {
    if (!mappedPayloadDraft.value) return;
    const parsed = Number(value);
    mappedPayloadDraft.value.expert_id = value && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    if (!mappedPayloadDraft.value.expert_id) mappedExpertSelectedItems.value = [];
  },
});
const {
  data: mappedExpertSearchData,
  isSearching: isSearchingMappedExperts,
  searchError: mappedExpertSearchError,
  hasResults: hasMappedExpertSearchResults,
} = useExpertSearch(mappedExpertSearchQuery);
const mappedExpertSearchResults = computed<ComboboxItemData[]>(() =>
  (mappedExpertSearchData.value ?? []).map((expert) => mappedExpertComboboxItem(expert.id, expert.name, expert.email, expert.avatar)),
);
const mappedExpertDetailId = computed(() => mappedExpertId.value ?? "");
const { expert: mappedExpertDetail, isLoading: isLoadingMappedExpertDetail } = useAdminExpertDetail(mappedExpertDetailId);
const isFetchingMappedExperts = computed(() => isSearchingMappedExperts.value || isLoadingMappedExpertDetail.value);
const showNoMappedExpertMatch = computed(
  () =>
    mappedExpertSearchQuery.value.trim().length >= 2 &&
    !isFetchingMappedExperts.value &&
    !hasMappedExpertSearchResults.value &&
    !mappedExpertId.value,
);

function setMappedExpertSelection(item: ComboboxItemData | null) {
  mappedExpertSelectedItems.value = item ? [item] : [];
  mappedExpertSearchQuery.value = item?.label ?? "";
}

function mappedExpertComboboxItem(id: number, name: string, email: string | null, avatar: string | null): ComboboxItemData {
  return {
    value: String(id),
    label: name,
    description: email ?? "",
    image: avatar ?? undefined,
    initials: generateInitials(name),
    rounded: true,
    showImage: true,
  };
}

function hydrateMappedExpertSelection() {
  const expert = mappedExpertDetail.value;
  if (!expert || String(expert.id) !== mappedExpertId.value) return;
  setMappedExpertSelection(mappedExpertComboboxItem(expert.id, expert.display_name, expert.email, expert.avatar));
}

watch(mappedExpertDetail, hydrateMappedExpertSelection, { immediate: true });

watch(mappedExpertSearchQuery, (query) => {
  const selectedExpert = mappedExpertSelectedItems.value[0];
  if (selectedExpert && query !== selectedExpert.label) {
    mappedExpertId.value = undefined;
    mappedExpertSelectedItems.value = [];
  }
});
const mappedLanguagesInput = computed({
  get: () => mappedPayloadDraft.value?.languages.join(", ") ?? "",
  set: (value: string) => {
    if (!mappedPayloadDraft.value) return;
    // Dedupe case-insensitively so the draft matches what the server's
    // Set-based normalization would persist.
    mappedPayloadDraft.value.languages = [
      ...new Set(
        value
          .split(",")
          .map((item) => item.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
  },
});
const mappedStartingPriceInput = computed({
  get: () => (typeof mappedPayloadDraft.value?.starting_price === "number" ? String(mappedPayloadDraft.value.starting_price) : ""),
  set: (value: string) => {
    if (!mappedPayloadDraft.value) return;
    const parsed = Number(value);
    mappedPayloadDraft.value.starting_price = value.trim() && Number.isFinite(parsed) ? parsed : null;
  },
});
const mappedImportMissingFields = computed(() => {
  const payload = mappedPayloadDraft.value;
  if (!payload) return [];

  const missingFields: string[] = [];
  const hasName = payload.expert_type === "agency" ? Boolean(payload.agency_name?.trim()) : Boolean(payload.first_name?.trim() && payload.last_name?.trim());
  if (!hasName) missingFields.push("name");
  if (!payload.bio?.trim()) missingFields.push("bio");
  if (!payload.country?.trim()) missingFields.push("country");
  if (!effectiveMappedSubcategorySlugs.value.length) missingFields.push("subcategories");
  return missingFields;
});
const globalMappedSubcategorySlugs = computed(() => {
  const record = selectedRecord.value;
  if (!record) return [];

  return mappedSubcategorySlugsFromGlobalMappings(record);
});
const effectiveMappedSubcategorySlugs = computed(() => {
  return uniqueStrings([...globalMappedSubcategorySlugs.value, ...(mappedPayloadDraft.value?.subcategory_slugs ?? [])]);
});
const globalMappedSubcategoryOptions = computed(() => {
  const selected = new Set(globalMappedSubcategorySlugs.value);
  return subcategoryOptions.value.filter((option) => selected.has(option.value));
});
const additionalSubcategoryOptions = computed(() => {
  const selected = new Set(mappedPayloadDraft.value?.subcategory_slugs ?? []);
  return subcategoryOptions.value.filter((option) => selected.has(option.value));
});
const effectiveSubcategoryOptions = computed(() => {
  const selected = new Set(effectiveMappedSubcategorySlugs.value);
  return subcategoryOptions.value.filter((option) => selected.has(option.value));
});
const addableSubcategoryOptions = computed(() => {
  const selected = new Set(effectiveMappedSubcategorySlugs.value);
  return subcategoryOptions.value.filter((option) => !selected.has(option.value));
});
const addableSubcategoryOptionGroups = computed<ComboboxGroupData[]>(() => {
  const selected = new Set(effectiveMappedSubcategorySlugs.value);
  return subcategoryOptionGroups.value
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !selected.has(item.value)),
    }))
    .filter((group) => group.items.length > 0);
});
const serviceMappingsBySourceValue = computed(() => new Map(serviceMappings.value.map((mapping) => [mapping.sourceValue, mapping])));
const mappingRows = computed(() => {
  const query = mappingSearch.value.trim().toLowerCase();
  const rows = serviceSourceValues.value
    .map((source) => ({ sourceValue: source.value, recordCount: source.recordCount, mapping: serviceMappingsBySourceValue.value.get(source.value) ?? null, stale: false }))
    .filter((row) => {
      if (query && !row.sourceValue.toLowerCase().includes(query)) return false;
      if (mappingFilter.value === "mapped") return Boolean(row.mapping?.targetValue);
      if (mappingFilter.value === "unmapped") return !row.mapping?.targetValue;
      return true;
    });

  return rows;
});
const staleMappingRows = computed(() => {
  const sourceValues = new Set(serviceSourceValues.value.map((source) => source.value));
  return serviceMappings.value
    .filter((mapping) => !sourceValues.has(mapping.sourceValue))
    .filter((mapping) => !mappingSearch.value.trim() || mapping.sourceValue.toLowerCase().includes(mappingSearch.value.trim().toLowerCase()))
    .filter((mapping) => {
      if (mappingFilter.value === "mapped") return Boolean(mapping.targetValue);
      if (mappingFilter.value === "unmapped") return !mapping.targetValue;
      return true;
    })
    .map((mapping) => ({ sourceValue: mapping.sourceValue, recordCount: 0, mapping, stale: true }));
});

const previewSubcategories = computed<SubcategoryDetails[]>(() => {
  return effectiveMappedSubcategorySlugs.value
    .map((slug) => subcategoryDetailsBySlug.value.get(slug))
    .filter((subcategory): subcategory is SubcategoryDetails => Boolean(subcategory));
});

const previewBody = computed<ShopexpertsPreviewExpert | null>(() => {
  const record = selectedRecord.value;
  const mapped = mappedPayloadDraft.value;
  if (!record || !mapped) return null;

  const profile = getProfile(record);
  const subcategories = previewSubcategories.value;

  const base = {
    slug: record.sourceSlug ?? profile.slug ?? "",
    bio: mapped.bio ?? "",
    country: mapped.country ?? "",
    starting_price: mapped.starting_price ?? 0,
    partner_tier: partnerTierFromScrapedTier(profile.tier),
    timezone: mapped.timezone ?? "",
    phone_no: mapped.phone_no ?? "",
    regions_served: selectedSupportedLocations.value,
    languages: mapped.languages,
    // Stored crawl media first: the scraped Shopify URL expires and would leave
    // the preview iframe with a broken image.
    profile_picture: partnerDirectoryAvatarUrl(record) ?? "",
    subcategories,
    social_links: { ...mapped.social_links },
    address: { address: record.primaryLocation ?? profile.primaryLocation ?? null },
  };

  const expertProfile: ShopexpertsPreviewExpert["profile"] =
    mapped.expert_type === "freelancer"
      ? { ...base, expert_type: "freelancer", first_name: mapped.first_name ?? "", last_name: mapped.last_name ?? "", role: null }
      : { ...base, expert_type: "agency", agency_name: mapped.agency_name ?? record.name ?? "" };

  const offeredServices = Object.values(
    subcategories.reduce((acc, subcategory) => {
      const categoryId = subcategory.category.id;
      if (!acc[categoryId]) {
        acc[categoryId] = {
          service_category_id: categoryId,
          service_subcategory_ids: [],
          meta: {
            service_category: subcategory.category.name,
            service_subcategories: [],
          },
        };
      }

      acc[categoryId]!.service_subcategory_ids.push(subcategory.id);
      acc[categoryId]!.meta.service_subcategories.push(subcategory.name);

      return acc;
    }, {} as Record<number, {
      service_category_id: number;
      service_subcategory_ids: number[];
      meta: {
        service_category: string;
        service_subcategories: string[];
      };
    }>),
  );

  const packagedServices = selectedSpecializedServices.value.map((service, index) => ({
    id: `scraped-service-${index}`,
    slug: `scraped-service-${index}`,
    service_name: service.name,
    thumbnail: "",
    gallery_images: [] as string[],
    starting_price: typeof service.priceAmountCents === "number" ? Math.round(service.priceAmountCents / 100) : 0,
    price_type: "fixed" as const,
    description: service.description,
  }));

  return {
    profile: expertProfile,
    offered_services: offeredServices,
    packaged_services: packagedServices,
    customer_stories: [],
    faqs: [],
  };
});

const previewReviews = computed<OverallReviews>(() => {
  const record = selectedRecord.value;
  const ratingSummary = selectedProfile.value?.ratingSummary;

  return {
    overall: {
      rating: clampRating(record?.rating ?? ratingSummary?.overallRating),
      reviews_count: Math.max(0, Math.round(record?.reviewCount ?? ratingSummary?.reviewCount ?? 0)),
    },
    breakdown: {
      quality_of_work: ratingFromBasisText(ratingSummary?.ratingBasis?.qualityOfWork),
      communication: ratingFromBasisText(ratingSummary?.ratingBasis?.communication),
      respects_timelines: 0,
      value_for_money: 0,
    },
  };
});

const previewCrawlerProfile = computed(() => {
  const record = selectedRecord.value;
  const expert = previewBody.value;
  if (!record || !expert) return null;

  return mapRecordToCrawlerProfilePayload(record, expert);
});

const selectedLogoAlt = computed(() => {
  const logo = selectedProfile.value?.logo;
  return logo?.alt ?? selectedRecord.value?.name ?? selectedProfile.value?.name ?? "";
});

const ratingDistribution = computed(() => {
  return (selectedProfile.value?.ratingSummary?.distribution ?? [])
    .filter((item): item is RatingDistributionEntry => typeof item.stars === "number" && typeof item.count === "number")
    .sort((a, b) => b.stars - a.stars);
});

const maxRatingDistributionCount = computed(() => {
  return Math.max(1, ...ratingDistribution.value.map((item) => item.count));
});

const selectedRecordJson = computed(() => {
  if (!selectedRecord.value) return "";
  return JSON.stringify(selectedRecord.value.rawPayload, null, 2);
});

// The filtered total drives pagination; the global total labels the Index tab.
const matchingRecordsCount = computed(() => importedRecordsTotal.value);
const indexedRecordsCount = computed(() => allImportedRecordsTotal.value);
const importedRecordsPageSize = computed(() => parseVisibleListLimit(importedRecordsLimit.value));
const importedRecordsPageCount = computed(() => Math.max(1, Math.ceil(matchingRecordsCount.value / importedRecordsPageSize.value)));
const importedRecordsRangeStart = computed(() => (matchingRecordsCount.value === 0 ? 0 : (importedRecordsPage.value - 1) * importedRecordsPageSize.value + 1));
const importedRecordsRangeEnd = computed(() => Math.min(importedRecordsPage.value * importedRecordsPageSize.value, matchingRecordsCount.value));
const hoveredRecordPosition = computed(() => formatRecordListPosition({
  index: hoveredRecordIndex.value,
  renderedCount: filteredRecords.value.length,
  page: importedRecordsPage.value,
  pageSize: importedRecordsPageSize.value,
  total: matchingRecordsCount.value,
}));
const canGoToPreviousImportedRecordsPage = computed(() => importedRecordsPage.value > 1 && !isLoadingImportedRecords.value);
const canGoToNextImportedRecordsPage = computed(() => importedRecordsPage.value < importedRecordsPageCount.value && !isLoadingImportedRecords.value);
const activeRecentTasksCount = computed(() => recentTasks.value.filter(isScrapeTaskPending).length);
const queueDiscoveredProfilesCount = computed(() => discoveredProfiles.value.filter((profile) => profile.status === "discovered").length);
const queueFailedProfilesCount = computed(() => discoveredProfiles.value.filter((profile) => profile.status === "scrape_failed").length);
const crawlerTabCount = computed(() => crawlerRunsCount.value + activeRecentTasksCount.value);
const apiHealthTag = computed(() => {
  if (apiHealthStatus.value === "online") return { text: "API online", variant: "primary" as const };
  if (apiHealthStatus.value === "offline") return { text: "API offline", variant: "dark" as const };
  return { text: "API checking", variant: "secondary" as const };
});
const apiMaxListLimit = computed(() => apiConfig.value?.maxListLimit ?? FALLBACK_MAX_LIST_LIMIT);
const crawlerSubtitle = "Crawl Shopify's partner directory, then queue scrapes to index experts";

const listLimitOptions = computed(() => {
  const max = apiMaxListLimit.value;
  const values = [10, 25, 50, 100, 250, 500, 1000].filter((value) => value <= max);
  if (!values.includes(max)) values.push(max);
  return values.map((value) => ({ label: `Show ${value}`, value: String(value) }));
});
const selectedDiscoveredProfiles = computed(() => discoveredProfiles.value.filter((profile) => selectedDiscoveredProfileIds.value.includes(profile.id)));
const topBarStatusLabel = computed(() => {
  if (activeTab.value === "crawler") return selectedCrawlRun.value ? `Crawler ${formatStatus(selectedCrawlRun.value.status)}` : apiHealthTag.value.text;
  if (activeTab.value === "mapping") return `${serviceMappings.value.length} mappings`;
  return selectedRecord.value ? selectedRecord.value.name ?? selectedRecord.value.sourceSlug ?? "Indexed expert" : `${indexedRecordsCount.value} indexed`;
});
const topBarSaveLabel = computed(() => (activeTab.value === "crawler" ? "Start crawl" : "Refresh"));
const selectedRecordImportMissingFields = computed(() => (selectedRecord.value ? importMissingFields(selectedRecord.value) : []));
const selectedRecordImportReadiness = computed(() => {
  if (!selectedRecord.value) return { text: "Missing", variant: "dark" as const };
  const missingFields = selectedRecordImportMissingFields.value;
  return {
    text: formatImportReadiness(selectedRecord.value),
    variant: missingFields.length ? ("dark" as const) : ("primary" as const),
  };
});
const screeningActions = [
  { label: "Approve", status: "approved" },
  { label: "Needs review", status: "needs_review" },
  { label: "Reject", status: "rejected" },
];

const selectedRecordFacts = computed(() => {
  const record = selectedRecord.value;
  if (!record) return [];

  const profile = getProfile(record);
  return [
    { label: "Primary location", value: record.primaryLocation ?? "Unknown" },
    { label: "Partner type", value: profile.partnerType ?? "Unknown" },
    {
      label: "Tier",
      value: profile.tier ? `${profile.tier} tier` : "None captured",
    },
    { label: "Rating", value: formatRating(record) },
    {
      label: "Price range",
      value: formatPrice(profile.priceRange, record.startingPriceCents),
    },
    { label: "Services", value: String(selectedRecordServices.value.length) },
    { label: "Partner since", value: profile.partnerSince ?? "Unknown" },
    { label: "Languages", value: formatList(selectedLanguages.value) },
    {
      label: "Supported locations",
      value: formatList(selectedSupportedLocations.value),
    },
  ];
});

// Every platform the scraper can capture, in profile-card order. Only LinkedIn
// and X have a mapper field, so this panel is the only place the rest surface
// outside the raw payload.
const SCRAPED_SOCIAL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn" },
  { key: "x", label: "X" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "youtube", label: "YouTube" },
  { key: "tiktok", label: "TikTok" },
] as const;

const selectedSocialLinks = computed(() => {
  const socialLinks = selectedProfile.value?.socialLinks;
  if (!socialLinks) return [];

  return SCRAPED_SOCIAL_PLATFORMS.flatMap(({ key, label }) => {
    const url = httpUrl(socialLinks[key]);
    return url ? [{ label, url }] : [];
  });
});

const selectedPageFacts = computed(() => {
  const payload = selectedPayload.value;

  return [
    {
      label: "Shopify slug",
      value: selectedRecord.value?.sourceSlug ?? selectedProfile.value?.slug ?? "Unknown",
    },
    { label: "Scraped at", value: formatDate(payload.scrapedAt) },
    { label: "Page title", value: payload.page?.title ?? "Unknown" },
    {
      label: "Meta description",
      value: payload.page?.metaDescription ?? "Unknown",
    },
  ];
});

const activeTaskMeta = computed(() => {
  const task = activeTask.value;
  if (!task) return [];

  return [
    { label: "Input", value: task.input },
    { label: "Slug", value: task.sourceSlug ?? "Unknown" },
    { label: "Started", value: formatDate(task.startedAt) },
    { label: "Finished", value: formatDate(task.finishedAt) },
  ];
});

async function loadSubcategoryOptions() {
  try {
    const categories = (await api.categories.getCategoriesWithSubcategories()) as CategoryWithSubcategories[];
    const options: SubcategoryOption[] = [];
    const detailsBySlug = new Map<string, SubcategoryDetails>();
    const optionGroups: ComboboxGroupData[] = [];

    for (const category of categories) {
      const groupItems = category.subcategories.map((subcategory) => {
        options.push({
          label: `${category.name} — ${subcategory.name}`,
          value: subcategory.slug,
        });
        detailsBySlug.set(subcategory.slug, {
          id: subcategory.id,
          name: subcategory.name,
          slug: subcategory.slug,
          category: { id: category.id, name: category.name, slug: category.slug },
        });

        return {
          label: subcategory.name,
          value: subcategory.slug,
        };
      });

      optionGroups.push({
        id: `category-${category.id ?? category.slug ?? category.name}`,
        label: category.name,
        items: groupItems,
      });
    }

    subcategoryOptions.value = options;
    subcategoryDetailsBySlug.value = detailsBySlug;
    subcategoryOptionGroups.value = optionGroups;
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load service subcategories:", error);
  }
}

function initializeMappedPayload(record: PartnerDirectoryRecord, derivedDefaults?: MappedExpertPayload | null) {
  if (derivedDefaults !== undefined) {
    selectedRecordDerivedDefaults.value = derivedDefaults ? cloneMappedPayload(derivedDefaults) : null;
  }

  const baseline = selectedRecordDerivedDefaults.value ?? prefillMappedPayload(record);
  const payload = record.mappedPayload ? mergeSparseMappedPayload(baseline, record.mappedPayload, record) : cloneMappedPayload(baseline);
  setMappedExpertSelection(null);
  mappedPayloadDraft.value = cloneMappedPayload(payload);
  mappedPayloadLastSaved.value = cloneMappedPayload(payload);
  mappedSubcategoryToAdd.value = "";
  hydrateMappedExpertSelection();
}

// The record's stored mappedPayload is sparse: a key exists only when the admin
// overrode the scraped default. Merge it over the og baseline for editing.
function mergeSparseMappedPayload(baseline: MappedExpertPayload, sparse: Record<string, unknown>, record: PartnerDirectoryRecord): MappedExpertPayload {
  const merged = cloneMappedPayload(baseline);
  merged.expert_id = null;
  merged.subcategory_slugs = [];

  const expertId = numberValue(sparse.expert_id);
  if (typeof expertId === "number" && Number.isInteger(expertId) && expertId > 0) merged.expert_id = expertId;
  if (sparse.expert_type === "agency" || sparse.expert_type === "freelancer") merged.expert_type = sparse.expert_type;
  if ("agency_name" in sparse) merged.agency_name = nullableString(sparse.agency_name);
  if ("first_name" in sparse) merged.first_name = nullableString(sparse.first_name);
  if ("last_name" in sparse) merged.last_name = nullableString(sparse.last_name);
  if ("bio" in sparse) merged.bio = nullableString(sparse.bio);
  if ("country" in sparse) merged.country = nullableString(sparse.country)?.toUpperCase() ?? null;
  if ("starting_price" in sparse) merged.starting_price = numberValue(sparse.starting_price);
  if ("timezone" in sparse) merged.timezone = nullableString(sparse.timezone);
  if ("phone_no" in sparse) merged.phone_no = sanitizePhoneNumber(sparse.phone_no);
  if ("languages" in sparse) merged.languages = uniqueStrings(toArray(sparse.languages)).map((language) => language.toLowerCase());

  const socialLinks = objectValue(sparse.social_links);
  if (socialLinks) {
    if ("website" in socialLinks) merged.social_links.website = nullableString(socialLinks.website);
    if ("shopify_directory_link" in socialLinks) merged.social_links.shopify_directory_link = nullableString(socialLinks.shopify_directory_link);
    if ("linkedin_url" in socialLinks) merged.social_links.linkedin_url = nullableString(socialLinks.linkedin_url);
    if ("x_url" in socialLinks) merged.social_links.x_url = nullableString(socialLinks.x_url);
  }

  const globalSubcategorySlugs = new Set(mappedSubcategorySlugsFromGlobalMappings(record));
  merged.subcategory_slugs = uniqueStrings(toArray(sparse.subcategory_slugs)).filter((slug) => !globalSubcategorySlugs.has(slug));

  return merged;
}

function prefillMappedPayload(record: PartnerDirectoryRecord): MappedExpertPayload {
  const profile = getProfile(record);
  const sourceName = nullableString(record.name ?? profile.name);
  const personalName = splitPersonalName(sourceName);
  const country = countryCodeFromDirectoryData(record, profile);
  const startingPriceCents = startingPriceCentsFromDirectoryData(record, profile);
  return {
    ...cloneMappedPayload(EMPTY_MAPPED_PAYLOAD),
    agency_name: sourceName,
    first_name: personalName?.firstName ?? null,
    last_name: personalName?.lastName ?? null,
    bio: nullableString(profile.about?.businessDescription),
    country,
    starting_price: typeof startingPriceCents === "number" ? startingPriceCents / 100 : null,
    timezone: timezoneFromDirectoryData(country, record, profile),
    phone_no: sanitizePhoneNumber(profile.contact?.phone),
    languages: languageCodesFromDirectoryData(profile),
    social_links: {
      website: nullableString(record.websiteUrl ?? profile.contact?.website),
      shopify_directory_link: nullableString(record.sourceUrl),
      linkedin_url: httpUrl(profile.socialLinks?.linkedin),
      x_url: httpUrl(profile.socialLinks?.x),
    },
    subcategory_slugs: [],
  };
}

function mappedSubcategorySlugsFromGlobalMappings(record: PartnerDirectoryRecord): string[] {
  const serviceMappingsMap = serviceMappingsBySourceValue.value;
  return uniqueStrings(servicesFromRecord(record).map((service) => serviceMappingsMap.get(service)?.targetValue).filter(Boolean));
}

function cloneMappedPayload(payload: MappedExpertPayload): MappedExpertPayload {
  return JSON.parse(JSON.stringify(payload)) as MappedExpertPayload;
}

function nullableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

// Mirrors the importer's `httpUrl`, so the Screener baseline matches what the
// backend will actually accept.
function httpUrl(value: unknown): string | null {
  const normalized = nullableString(value);
  if (!normalized) return null;

  try {
    const url = new URL(normalized);
    return url.protocol === "http:" || url.protocol === "https:" ? normalized : null;
  } catch {
    return null;
  }
}

// Scraped phones come from raw tel: hrefs, so they can carry percent-encoding
// and invisible Unicode bidi control characters (U+202A/U+202C etc.).
function sanitizePhoneNumber(value: unknown): string | null {
  const normalized = nullableString(value);
  if (!normalized) return null;

  let decoded = normalized;
  try {
    decoded = decodeURIComponent(normalized);
  } catch {
    // keep the raw value if it is not valid percent-encoding
  }

  const cleaned = decoded
    .replace(/[\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || null;
}

function normalizeLookupValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function selectValue(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function countryCodeFromName(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  if (!normalized) return null;
  if (/^[a-z]{2}$/i.test(normalized)) return normalized.toUpperCase();
  return COUNTRY_CODES_BY_NAME.get(normalizeLookupValue(normalized)) ?? null;
}

function countryCodeFromDirectoryData(record: PartnerDirectoryRecord, profile: ScrapedProfile): string | null {
  const candidates = [
    record.country,
    record.primaryLocation,
    profile.primaryLocation,
    ...uniqueStrings(toArray(profile.supportedLocations)),
  ];

  for (const candidate of candidates) {
    const country = countryCodeFromLocation(candidate);
    if (country) return country;
  }

  return null;
}

function countryCodeFromLocation(value: string | null | undefined): string | null {
  const normalized = nullableString(value);
  if (!normalized) return null;

  const directCountry = countryCodeFromName(normalized);
  if (directCountry) return directCountry;

  const parts = normalized.split(",").map((part) => part.trim()).filter(Boolean);
  for (const part of parts.slice().reverse()) {
    const country = countryCodeFromName(part);
    if (country) return country;
  }

  return null;
}

function languageCodesFromDirectoryData(profile: ScrapedProfile): string[] {
  return uniqueStrings(toArray(profile.languages))
    .map((language) => languageCodeFromName(language))
    .filter((language): language is string => Boolean(language));
}

function languageCodeFromName(value: string): string | null {
  const normalized = nullableString(value);
  if (!normalized) return null;
  if (/^[a-z]{2,3}$/i.test(normalized)) return normalized.toLowerCase();
  return LANGUAGE_CODES_BY_NAME.get(normalizeLookupValue(normalized)) ?? null;
}

function startingPriceCentsFromDirectoryData(record: PartnerDirectoryRecord, profile: ScrapedProfile): number | null {
  if (typeof record.startingPriceCents === "number") return record.startingPriceCents;
  if (typeof profile.priceRange?.amountCents === "number") return profile.priceRange.amountCents;

  const servicePrices = toArray(profile.specializedServices)
    .map((service) => objectValue(service))
    .map((service) => objectValue(service?.price))
    .map((price) => numberValue(price?.amountCents))
    .filter((price): price is number => typeof price === "number" && price >= 0);

  if (!servicePrices.length) return null;
  return Math.min(...servicePrices);
}

function timezoneFromDirectoryData(countryCode: string | null, record: PartnerDirectoryRecord, profile: ScrapedProfile): string | null {
  if (!countryCode) return null;

  const location = nullableString(record.primaryLocation ?? profile.primaryLocation);
  const regionCode = regionCodeFromLocation(location);
  if (countryCode === "US" && regionCode && US_TIMEZONE_BY_STATE[regionCode]) return US_TIMEZONE_BY_STATE[regionCode];
  if (countryCode === "CA" && regionCode && CANADA_TIMEZONE_BY_PROVINCE[regionCode]) return CANADA_TIMEZONE_BY_PROVINCE[regionCode];

  return DEFAULT_TIMEZONE_BY_COUNTRY_CODE[countryCode] ?? null;
}

function regionCodeFromLocation(value: string | null): string | null {
  const parts = value?.split(",").map((part) => part.trim()).filter(Boolean) ?? [];
  if (parts.length < 2) return null;

  for (const part of parts.slice(0, -1).reverse()) {
    const match = part.match(/\b[A-Z]{2}\b/);
    if (match) return match[0];
  }

  return null;
}

function splitPersonalName(value: string | null): { firstName: string; lastName: string } | null {
  const normalized = nullableString(value);
  if (!normalized || /[&/|+]|\b(agency|apps?|consulting|designs?|developers?|development|digital|ecommerce|e-commerce|group|labs?|marketing|media|solutions?|studio|team|technologies|web)\b/i.test(normalized)) {
    return null;
  }

  const nameParts = normalized.split(/\s+/).filter(Boolean);
  if (nameParts.length < 2 || nameParts.length > 4) return null;

  return {
    firstName: nameParts[0] ?? "",
    lastName: nameParts.slice(1).join(" "),
  };
}

function setMappedCountry(value: string | undefined) {
  if (!mappedPayloadDraft.value) return;
  mappedPayloadDraft.value.country = value?.trim() ? value.trim().toUpperCase().slice(0, 2) : null;
}

function setMappedTextField(field: "agency_name" | "first_name" | "last_name" | "bio" | "timezone" | "phone_no", value: string | undefined) {
  if (!mappedPayloadDraft.value) return;
  mappedPayloadDraft.value[field] = nullableString(value);
}

function setMappedSocialLink(field: keyof MappedExpertPayload["social_links"], value: string | undefined) {
  if (!mappedPayloadDraft.value) return;
  mappedPayloadDraft.value.social_links[field] = nullableString(value);
}

function addMappedSubcategory(value: string | undefined) {
  if (!mappedPayloadDraft.value || !value) return;
  if (!mappedPayloadDraft.value.subcategory_slugs.includes(value)) {
    mappedPayloadDraft.value.subcategory_slugs = [...mappedPayloadDraft.value.subcategory_slugs, value];
  }
  mappedSubcategoryToAdd.value = "";
}

function removeMappedSubcategory(value: string) {
  if (!mappedPayloadDraft.value) return;
  mappedPayloadDraft.value.subcategory_slugs = mappedPayloadDraft.value.subcategory_slugs.filter((slug) => slug !== value);
}

function resetMappedPayload() {
  if (mappedPayloadLastSaved.value) {
    setMappedExpertSelection(null);
    mappedPayloadDraft.value = cloneMappedPayload(mappedPayloadLastSaved.value);
    hydrateMappedExpertSelection();
  } else if (selectedRecord.value) {
    initializeMappedPayload(selectedRecord.value);
  }
}

async function saveMappedPayload() {
  const record = selectedRecord.value;
  if (!record || !mappedPayloadDraft.value || isSavingMappedPayload.value) return;

  isSavingMappedPayload.value = true;
  errorMessage.value = null;

  try {
    // Only the sparse diff against the og baseline is persisted; the server
    // re-normalizes it with the same comparison rules.
    const payload = mappedOverrides.value;
    const updatedRecord = await updatePartnerDirectoryRecordMappedPayload(record.id, payload);
    selectedRecord.value = updatedRecord;
    selectedRecordId.value = updatedRecord.id;
    importedRecords.value = importedRecords.value.map((existingRecord) => (existingRecord.id === updatedRecord.id ? updatedRecord : existingRecord));
    initializeMappedPayload(updatedRecord);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to save mapped data.");
  } finally {
    isSavingMappedPayload.value = false;
  }
}

async function loadSelectedRecordNotes() {
  const record = selectedRecord.value;
  if (!record) {
    notes.value = [];
    return;
  }

  isLoadingNotes.value = true;
  try {
    notes.value = await listPartnerDirectoryRecordNotes(record.id);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to load screener notes.");
  } finally {
    isLoadingNotes.value = false;
  }
}

async function addRecordNote() {
  const record = selectedRecord.value;
  const body = noteDraft.value.trim();
  if (!record || !body || isSavingNote.value) return;

  isSavingNote.value = true;
  errorMessage.value = null;

  try {
    const note = await createPartnerDirectoryRecordNote(record.id, body);
    notes.value = [note, ...notes.value];
    noteDraft.value = "";
    setRecordNoteCount(record.id, notes.value.length);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to add screener note.");
  } finally {
    isSavingNote.value = false;
  }
}

async function deleteRecordNote(note: PartnerDirectoryRecordNote) {
  const recordId = note.recordId;
  if (deletingNoteIds.value.includes(note.id)) return;

  deletingNoteIds.value = [...deletingNoteIds.value, note.id];
  errorMessage.value = null;

  try {
    await deletePartnerDirectoryRecordNote(note.id);
    notes.value = notes.value.filter((existingNote) => existingNote.id !== note.id);
    setRecordNoteCount(recordId, notes.value.length);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to delete screener note.");
  } finally {
    deletingNoteIds.value = deletingNoteIds.value.filter((id) => id !== note.id);
  }
}

function setRecordNoteCount(recordId: string, count: number) {
  importedRecords.value = importedRecords.value.map((record) => (record.id === recordId ? { ...record, noteCount: count } : record));
  if (selectedRecord.value?.id === recordId) {
    selectedRecord.value = { ...selectedRecord.value, noteCount: count };
  }
}

async function refreshMappings() {
  isLoadingMappings.value = true;
  errorMessage.value = null;

  try {
    const [sourceValues, mappings] = await Promise.all([listPartnerDirectoryServiceSourceValues(), listPartnerDirectoryGlobalMappings({ kind: SERVICE_MAPPING_KIND })]);
    serviceSourceValues.value = [...sourceValues].sort((a, b) => b.recordCount - a.recordCount || a.value.localeCompare(b.value));
    serviceMappings.value = mappings;
    hasLoadedMappings.value = true;
    if (selectedRecord.value && !selectedRecord.value.mappedPayload) {
      initializeMappedPayload(selectedRecord.value);
    }
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to load global mappings.");
  } finally {
    isLoadingMappings.value = false;
  }
}

async function updateServiceMapping(sourceValue: string, targetValue: string | undefined) {
  const existingMapping = serviceMappingsBySourceValue.value.get(sourceValue);
  const previousMappings = serviceMappings.value;
  const optimisticMapping: PartnerDirectoryGlobalMapping = existingMapping
    ? { ...existingMapping, targetValue: targetValue || null }
    : {
        id: `optimistic-${sourceValue}`,
        kind: SERVICE_MAPPING_KIND,
        sourceValue,
        targetValue: targetValue || null,
        notes: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
  serviceMappings.value = [...serviceMappings.value.filter((mapping) => mapping.sourceValue !== sourceValue), optimisticMapping];

  try {
    const savedMapping = await upsertPartnerDirectoryGlobalMapping({ kind: SERVICE_MAPPING_KIND, sourceValue, targetValue: targetValue || null });
    serviceMappings.value = [...serviceMappings.value.filter((mapping) => mapping.sourceValue !== sourceValue), savedMapping];
  } catch (error) {
    serviceMappings.value = previousMappings;
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to save global mapping.");
  }
}

async function clearServiceMapping(mapping: PartnerDirectoryGlobalMapping) {
  const previousMappings = serviceMappings.value;
  serviceMappings.value = serviceMappings.value.filter((existingMapping) => existingMapping.id !== mapping.id);

  try {
    await deletePartnerDirectoryGlobalMapping(mapping.id);
  } catch (error) {
    serviceMappings.value = previousMappings;
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to remove global mapping.");
  }
}

async function startCrawlRun() {
  if (!canStartCrawl.value) return;

  isStartingCrawl.value = true;
  errorMessage.value = null;
  crawlerMessage.value = null;

  try {
    const run = await createPartnerDirectoryCrawlRun({
      seedUrl: crawlSeedUrl.value.trim() || DEFAULT_CRAWL_SEED_URL,
      options: {
        maxPages: parseOptionalPositiveInteger(crawlMaxPages.value),
        maxProfiles: parseOptionalPositiveInteger(crawlMaxProfiles.value),
        enqueueScrapeTasks: crawlQueueScrapeTasks.value,
        scrapeForce: crawlQueueScrapeTasks.value && crawlForceScrapeRefresh.value,
        queueLimit: parseOptionalPositiveInteger(crawlAutoQueueLimit.value),
      },
    });

    await refreshCrawlRuns({ autoSelect: false });
    await selectCrawlRun(run);
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to start crawler run.");
  } finally {
    isStartingCrawl.value = false;
  }
}

function commitCrawlerSeedUrl(value: string) {
  crawlSeedUrl.value = value.trim();
}

async function runTopBarAction() {
  if (activeTab.value === "crawler") {
    await startCrawlRun();
    return;
  }

  await refreshPageDataAndSchedulePoll();
}

async function startScrapeTask() {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  errorMessage.value = null;
  resultRecord.value = null;
  shouldAutoSelectResultRecord.value = true;

  try {
    const response = await createPartnerDirectoryScrapeTask({
      input: input.value.trim(),
      force: force.value,
    });
    activeTask.value = await getPartnerDirectoryScrapeTask(response.taskId);
    await refreshPageData();
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to start scraper task.");
  } finally {
    isSubmitting.value = false;
  }
}

async function refreshRecentTasks() {
  try {
    recentTasks.value = await listPartnerDirectoryScrapeTasks({ limit: parseVisibleListLimit(recentTasksLimit.value) });
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load scrape tasks:", error);
  }
}

async function refreshApiConfig() {
  try {
    apiConfig.value = await getPartnerDirectoryApiConfig();
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load partner directory API config:", error);
  }
}

async function refreshApiHealth() {
  try {
    const health = await checkPartnerDirectoryApiHealth();
    apiHealthStatus.value = health.ok ? "online" : "offline";
  } catch {
    apiHealthStatus.value = "offline";
  }
}

async function refreshCrawlRuns(options: { autoSelect?: boolean } = {}) {
  try {
    crawlRuns.value = await listPartnerDirectoryCrawlRuns({ limit: parseVisibleListLimit(crawlRunsLimit.value) });

    if (!selectedCrawlRunId.value && options.autoSelect !== false && crawlRuns.value[0]) {
      await selectCrawlRun(crawlRuns.value[0], { auto: true });
    } else if (selectedCrawlRunId.value) {
      selectedCrawlRun.value = crawlRuns.value.find((run) => run.id === selectedCrawlRunId.value) ?? selectedCrawlRun.value;
    }
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load crawl runs:", error);
  }
}

async function recoverScrapeQueue() {
  if (isRecoveringQueue.value) return;

  isRecoveringQueue.value = true;
  queueRecoveryMessage.value = null;
  errorMessage.value = null;

  try {
    const result = await recoverPartnerDirectoryScrapeTasks();
    queueRecoveryMessage.value = result.requeued > 0 ? `${result.requeued} stuck scrape tasks requeued.` : "No stuck scrape tasks found.";
    await refreshRecentTasks();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to recover the scrape queue.");
  } finally {
    isRecoveringQueue.value = false;
  }
}

async function repairReviewCountMismatches() {
  if (isRepairingReviewCounts.value) return;

  isRepairingReviewCounts.value = true;
  reviewRepairMessage.value = null;
  errorMessage.value = null;

  try {
    const result = await repairPartnerDirectoryReviewCountMismatches();
    reviewRepairMessage.value =
      result.matched > 0
        ? `${result.scheduled} inconsistent ${result.scheduled === 1 ? "profile" : "profiles"} scheduled for re-scraping.`
        : "No review-count inconsistencies found.";
    await refreshRecentTasks();
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to repair review-count inconsistencies.");
  } finally {
    isRepairingReviewCounts.value = false;
  }
}

async function refreshRecordCountries() {
  try {
    const result = await listPartnerDirectoryRecordCountries();
    recordCountryValues.value = result.countries;
    hasUnknownRecordCountry.value = result.hasUnknownCountry;
    allImportedRecordsTotal.value = result.total;
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load record countries:", error);
  }
}

function recordCountriesForQuery(): string[] | undefined {
  const availableCountries = recordCountryOptions.value.map((option) => option.value);
  if (availableCountries.length === 0) return undefined;

  const selectedCountries = new Set(recordCountryFilter.value);
  if (availableCountries.every((country) => selectedCountries.has(country))) return undefined;
  return availableCountries.filter((country) => selectedCountries.has(country));
}

async function refreshImportedRecords() {
  if (isLoadingImportedRecords.value) {
    importedRecordsRefreshQueued = true;
    return;
  }

  let reloadAtClampedPage = false;
  isLoadingImportedRecords.value = true;
  try {
    const limit = importedRecordsPageSize.value;
    const { records, total } = await listPartnerDirectoryRecords({
      limit,
      offset: (importedRecordsPage.value - 1) * limit,
      search: recordSearch.value.trim(),
      status: recordStatusFilter.value,
      countries: recordCountriesForQuery(),
      minRating: Number(recordMinRating.value),
      minReviews: Number(recordMinReviews.value),
      sort: recordSort.value,
    });

    const pageCount = Math.max(1, Math.ceil(total / limit));
    if (importedRecordsPage.value > pageCount) {
      importedRecordsTotal.value = total;
      importedRecordsPage.value = pageCount;
      reloadAtClampedPage = true;
      return;
    }

    importedRecords.value = records;
    importedRecordsTotal.value = total;
    // Prune on every refresh (not just after bulk actions) so a queued or
    // polled reload can never leave checkboxes pointing at hidden rows.
    pruneRecordSelection();

    if (!selectedRecordId.value && importedRecords.value[0]) {
      await selectImportedRecord(importedRecords.value[0], { auto: true });
    } else if (selectedRecordId.value) {
      selectedRecord.value = importedRecords.value.find((record) => record.id === selectedRecordId.value) ?? selectedRecord.value;
    }
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load imported records:", error);
  } finally {
    isLoadingImportedRecords.value = false;
    if (reloadAtClampedPage || importedRecordsRefreshQueued) {
      importedRecordsRefreshQueued = false;
      void refreshImportedRecords();
    }
  }
}

async function refreshIndexedRecords() {
  await Promise.all([refreshRecordCountries(), refreshImportedRecords()]);
}

async function goToImportedRecordsPage(page: number) {
  const nextPage = Math.min(Math.max(1, page), importedRecordsPageCount.value);
  if (nextPage === importedRecordsPage.value || isLoadingImportedRecords.value) return;

  importedRecordsPage.value = nextPage;
  hoveredRecordIndex.value = null;
  selectedRecordId.value = null;
  selectedRecord.value = null;
  clearRecordSelection();
  await refreshImportedRecords();
}

async function refreshPageData() {
  await refreshApiHealth();

  if (apiHealthStatus.value === "offline") {
    return;
  }

  await refreshApiConfig();
  await Promise.all([refreshRecentTasks(), refreshRecordCountries(), refreshImportedRecords(), refreshCrawlRuns({ autoSelect: true }), refreshImportJobs()]);
}

async function refreshPageDataAndSchedulePoll() {
  await refreshPageData();
  schedulePoll();
}

async function selectTask(taskId: string) {
  clearPoll();
  errorMessage.value = null;
  resultRecord.value = null;
  shouldAutoSelectResultRecord.value = true;

  try {
    activeTask.value = await getPartnerDirectoryScrapeTask(taskId);
    await loadResultRecord();
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to load scraper task.");
  }
}

async function refreshImportJobs() {
  const page = importJobsPage.value;
  const loadToken = importJobsLoadToken.value + 1;
  importJobsLoadToken.value = loadToken;

  try {
    const { jobs, total } = await listPartnerDirectoryImportJobs({
      limit: IMPORT_JOBS_PAGE_SIZE,
      offset: (page - 1) * IMPORT_JOBS_PAGE_SIZE,
    });

    if (importJobsLoadToken.value !== loadToken || importJobsPage.value !== page) return;

    importJobs.value = jobs;
    importJobsTotal.value = total;

    const trackedIds = new Set(activeImportJobs.value.map((job) => job.id));
    const untracked = jobs.filter((job) => isImportJobActive(job) && !trackedIds.has(job.id));
    if (untracked.length) activeImportJobs.value = [...activeImportJobs.value, ...untracked];
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load import jobs:", error);
  }
}

function isImportJobActive(job: PartnerDirectoryImportJob): boolean {
  return job.status === "queued" || job.status === "running";
}

async function pollActiveImportJobs() {
  const tracked = activeImportJobs.value;
  if (!tracked.length) return;
  const pollToken = ++activeImportJobPollToken;

  const latestJobs = await Promise.all(tracked.map((job) => getPartnerDirectoryImportJob(job.id)));
  if (pollToken !== activeImportJobPollToken) return;

  const latestById = new Map(latestJobs.map((job) => [job.id, job]));
  importJobs.value = importJobs.value.map((existing) => latestById.get(existing.id) ?? existing);
  const latestSelected = selectedImportJobId.value ? latestById.get(selectedImportJobId.value) : undefined;
  if (latestSelected) selectedImportJobSnapshot.value = latestSelected;
  // Mapped over the current list, not the polled snapshot: jobs started or
  // cancelled while the requests were in flight must keep that state.
  activeImportJobs.value = activeImportJobs.value.map((job) => latestById.get(job.id) ?? job).filter(isImportJobActive);

  // Terminal transition: one final refresh so the job row and its records land
  // in their finished state even if the user is on another history page.
  if (latestJobs.some((job) => !isImportJobActive(job))) await refreshImportJobs();
  if (selectedImportJobId.value && latestById.has(selectedImportJobId.value)) await refreshSelectedImportJobRecords();
}

async function goToImportJobsPage(page: number) {
  const nextPage = Math.min(Math.max(1, page), importJobsPageCount.value);
  if (nextPage === importJobsPage.value) return;
  importJobsPage.value = nextPage;
  await refreshImportJobs();
}

async function startImportJob() {
  if (isStartingImportJob.value) return;

  isStartingImportJob.value = true;
  errorMessage.value = null;

  try {
    const job = await createPartnerDirectoryImportJob({ targetProfileStatus: newImportJobTargetStatus.value });
    activeImportJobs.value = [...activeImportJobs.value, job];
    importJobsPage.value = 1;
    importJobs.value = [job, ...importJobs.value].slice(0, IMPORT_JOBS_PAGE_SIZE);
    importJobsTotal.value += 1;
    await selectImportJob(job);
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to start the import job.");
  } finally {
    isStartingImportJob.value = false;
  }
}

async function cancelImportJob(jobId: string) {
  if (isCancellingImportJob.value) return;

  isCancellingImportJob.value = true;
  errorMessage.value = null;

  try {
    const job = await cancelPartnerDirectoryImportJob(jobId);
    importJobs.value = importJobs.value.map((existing) => (existing.id === job.id ? job : existing));
    activeImportJobs.value = activeImportJobs.value.filter((existing) => existing.id !== job.id);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to cancel the import job.");
  } finally {
    isCancellingImportJob.value = false;
  }
}

async function selectImportJob(job: PartnerDirectoryImportJob) {
  selectedImportJobId.value = job.id;
  selectedImportJobSnapshot.value = job;
  selectedImportJobRecordsOutcome.value = job.recordsFailed > 0 ? "failed" : job.recordsSkipped > 0 ? "skipped" : "all";
  selectedImportJobRecordsPage.value = 1;
  selectedImportJobRecords.value = [];
  selectedImportJobRecordsTotal.value = 0;
  await refreshSelectedImportJobRecords();
}

async function refreshSelectedImportJobRecords() {
  const jobId = selectedImportJobId.value;
  if (!jobId) return;

  const loadToken = selectedImportJobRecordsLoadToken.value + 1;
  selectedImportJobRecordsLoadToken.value = loadToken;
  isLoadingImportJobRecords.value = true;

  try {
    const { records, total } = await listPartnerDirectoryImportJobRecords(jobId, {
      limit: IMPORT_JOB_RECORDS_PAGE_SIZE,
      offset: (selectedImportJobRecordsPage.value - 1) * IMPORT_JOB_RECORDS_PAGE_SIZE,
      outcome: selectedImportJobRecordsOutcome.value === "all" ? undefined : selectedImportJobRecordsOutcome.value,
    });

    // A newer selection or page change owns the list now; drop this response.
    if (selectedImportJobRecordsLoadToken.value !== loadToken || selectedImportJobId.value !== jobId) return;

    selectedImportJobRecords.value = records;
    selectedImportJobRecordsTotal.value = total;
  } catch (error) {
    if (selectedImportJobRecordsLoadToken.value === loadToken) {
      errorMessage.value = formatPartnerDirectoryError(error, "Failed to load this job's records.");
    }
  } finally {
    if (selectedImportJobRecordsLoadToken.value === loadToken) {
      isLoadingImportJobRecords.value = false;
    }
  }
}

async function setImportJobRecordsOutcome(value: string | string[] | undefined) {
  const outcome = (typeof value === "string" ? value : "all") as PartnerDirectoryImportJobRecordOutcome | "all";
  if (outcome === selectedImportJobRecordsOutcome.value) return;
  selectedImportJobRecordsOutcome.value = outcome;
  selectedImportJobRecordsPage.value = 1;
  await refreshSelectedImportJobRecords();
}

function importJobRecordResolvedSince(row: PartnerDirectoryImportJobRecord): boolean {
  const lastImportedAt = row.record?.lastImportedAt;
  return row.outcome !== "imported" && !!lastImportedAt && new Date(lastImportedAt) > new Date(row.createdAt);
}

function canRetryImportJobRecord(row: PartnerDirectoryImportJobRecord): boolean {
  return row.outcome !== "imported" && (row.record?.status === "approved" || row.record?.status === "imported");
}

async function openImportJobRecordInIndex(record: PartnerDirectoryRecord) {
  activeTab.value = "index";
  await selectImportedRecord(record);
}

async function goToImportJobRecordsPage(page: number) {
  const nextPage = Math.min(Math.max(1, page), selectedImportJobRecordsPageCount.value);
  if (nextPage === selectedImportJobRecordsPage.value) return;
  selectedImportJobRecordsPage.value = nextPage;
  await refreshSelectedImportJobRecords();
}

function applyRecordEverywhere(updated: PartnerDirectoryRecord) {
  selectedImportJobRecords.value = selectedImportJobRecords.value.map((row) => (row.recordId === updated.id ? { ...row, record: updated } : row));
  importedRecords.value = importedRecords.value.map((existing) => (existing.id === updated.id ? updated : existing));
  if (selectedRecord.value?.id === updated.id) selectedRecord.value = updated;
}

async function syncRecordToPlatform(record: PartnerDirectoryRecord, options: { targetProfileStatus?: PartnerDirectoryExpertProfileStatus } = {}) {
  if (syncingRecordId.value) return;

  const recordName = record.name ?? record.sourceSlug ?? "this record";
  const claimedNote = record.platformClaimed ? " This profile is CLAIMED on the platform: only reviews and confidence signals sync, profile fields stay untouched." : "";
  const confirmed = record.status === "imported"
    ? window.confirm(`Re-sync "${recordName}" to the platform? The expert's publish status won't change, and fields protected by overrides stay as they are.${claimedNote}`)
    : window.confirm(
        options.targetProfileStatus === "draft"
          ? `Create "${recordName}" on the platform as a DRAFT expert (not publicly visible)?${claimedNote}`
          : `Create "${recordName}" on the platform as a PUBLISHED expert? Use the Import tab to bulk-import as draft instead.${claimedNote}`,
      );
  if (!confirmed) return;

  syncingRecordId.value = record.id;
  errorMessage.value = null;

  try {
    const updated = await syncPartnerDirectoryRecord(record.id, options.targetProfileStatus ? { targetProfileStatus: options.targetProfileStatus } : {});
    applyRecordEverywhere(updated);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to sync this record to the platform.");
    try {
      applyRecordEverywhere((await getPartnerDirectoryRecord(record.id)).record);
    } catch {
      // the error banner already explains the failure
    }
  } finally {
    syncingRecordId.value = null;
    importJobsPage.value = 1;
    await refreshImportJobs();
  }
}

async function revertRecordImport(record: PartnerDirectoryRecord) {
  if (revertingRecordId.value) return;

  const recordName = record.name ?? record.sourceSlug ?? "this record";
  if (!window.confirm(`Revert the import of "${recordName}"? This removes the created expert from the platform entirely: profile, reviews, social links, address, and avatar.`)) {
    return;
  }

  revertingRecordId.value = record.id;
  errorMessage.value = null;

  try {
    const updated = await revertPartnerDirectoryRecordImport(record.id);
    applyRecordEverywhere(updated);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to revert this expert's import.");
  } finally {
    revertingRecordId.value = null;
  }
}

async function rescrapeSelectedRecord() {
  const record = selectedRecord.value;
  if (!record || isRescrapingRecord.value) return;

  isRescrapingRecord.value = true;
  errorMessage.value = null;
  recordActionMessage.value = null;

  try {
    const result = await rescrapePartnerDirectoryRecord(record.id);
    recordActionMessage.value = `Re-scrape ${formatStatus(result.status)} for "${record.name ?? record.sourceSlug ?? "this record"}".`;
    // Track the task so the existing polling loop surfaces its progress.
    shouldAutoSelectResultRecord.value = false;
    activeTask.value = await getPartnerDirectoryScrapeTask(result.taskId);
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to queue a re-scrape for this record.");
  } finally {
    isRescrapingRecord.value = false;
  }
}

async function runBulkRecordAction(action: (recordIds: string[]) => Promise<string>) {
  const recordIds = [...selectedRecordIds.value];
  if (!recordIds.length || isRunningBulkAction.value) return;

  isRunningBulkAction.value = true;
  errorMessage.value = null;
  bulkActionMessage.value = null;

  try {
    bulkActionMessage.value = await action(recordIds);
    await refreshIndexedRecords();

    // A bulk status change can push the open record out of the filtered list;
    // re-fetch its detail so the pane keeps showing fresh state.
    const selectedId = selectedRecordId.value;
    if (selectedId && recordIds.includes(selectedId) && !importedRecords.value.some((record) => record.id === selectedId)) {
      try {
        applyRecordEverywhere((await getPartnerDirectoryRecord(selectedId)).record);
      } catch {
        // The list refresh above stays authoritative; the pane keeps its last state.
      }
    }
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to run this bulk action.");
  } finally {
    isRunningBulkAction.value = false;
  }
}

async function bulkRescrapeSelectedRecords() {
  await runBulkRecordAction(async (recordIds) => {
    const result = await bulkRescrapePartnerDirectoryRecords(recordIds);
    schedulePoll();
    const parts = [`${result.scheduled} re-scrapes queued`];
    if (result.skippedActive > 0) parts.push(`${result.skippedActive} already scraping`);
    if (result.missing > 0) parts.push(`${result.missing} missing`);
    return `${parts.join(", ")}.`;
  });
}

async function bulkUpdateSelectedRecordsStatus(status: PartnerDirectoryBulkRecordStatus) {
  await runBulkRecordAction(async (recordIds) => {
    const result = await bulkUpdatePartnerDirectoryRecordStatus(recordIds, status);
    const parts = [`${result.updated} records set to ${formatStatus(status)}`];
    if (result.missing > 0) parts.push(`${result.missing} missing`);
    return `${parts.join(", ")}.`;
  });
}

async function bulkSyncSelectedRecords() {
  const count = selectedRecordIds.value.size;
  if (!count) return;

  const targetStatus = bulkSyncTargetStatus.value;
  const statusCopy =
    targetStatus === "draft"
      ? "Newly created experts are imported as DRAFTS (not publicly visible)."
      : "Newly created experts go live as PUBLISHED profiles.";
  const confirmed = window.confirm(
    `Sync ${count} selected record${count === 1 ? "" : "s"} to the platform? ${statusCopy} Only records with status approved or imported are eligible; claimed profiles only receive reviews and confidence signals.`,
  );
  if (!confirmed) return;

  await runBulkRecordAction(async (recordIds) => {
    const result = await bulkSyncPartnerDirectoryRecords(recordIds, { targetProfileStatus: targetStatus });
    const jobsAhead = activeImportJobs.value.length;
    activeImportJobs.value = [...activeImportJobs.value, result.job];
    importJobsPage.value = 1;
    await refreshImportJobs();
    schedulePoll();
    const eligibleCopy = `${result.eligible} eligible record${result.eligible === 1 ? "" : "s"}`;
    const parts = [jobsAhead > 0 ? `Import job queued for ${eligibleCopy} behind ${jobsAhead} active job${jobsAhead === 1 ? "" : "s"}` : `Import job started for ${eligibleCopy}`];
    if (result.ineligible > 0) parts.push(`${result.ineligible} ineligible`);
    return `${parts.join(", ")}.`;
  });
}

function canRevertImportJob(job: PartnerDirectoryImportJob): boolean {
  const isTerminal = job.status === "succeeded" || job.status === "failed" || job.status === "cancelled";
  return isTerminal && !job.revertedAt && job.recordsSucceeded > 0;
}

function formatImportJobRevertSummary(result: PartnerDirectoryImportJobRevertResult): string {
  const totals = result.totals;
  return `Reverted: ${totals.experts_deleted} experts deleted, ${totals.actions_reverted} fields reverted, ${totals.actions_skipped} actions skipped, ${totals.experts_skipped} experts skipped.`;
}

async function revertImportJob(job: PartnerDirectoryImportJob) {
  if (revertingJobId.value) return;

  const confirmed = window.confirm(
    `Revert the import job from ${formatDate(job.createdAt)}? Experts this job created are deleted, and fields it changed are restored only when they still hold the imported value. Claimed profiles are never touched.`,
  );
  if (!confirmed) return;

  revertingJobId.value = job.id;
  errorMessage.value = null;
  importJobRevertMessage.value = null;
  importJobRevertError.value = null;

  try {
    const { job: updatedJob, result } = await revertPartnerDirectoryImportJob(job.id);
    importJobs.value = importJobs.value.map((existing) => (existing.id === updatedJob.id ? updatedJob : existing));
    importJobRevertMessage.value = formatImportJobRevertSummary(result);
    await refreshIndexedRecords();
    if (selectedImportJobId.value === updatedJob.id) await refreshSelectedImportJobRecords();
  } catch (error) {
    // 409 bodies ({error}) — e.g. "job predates the action registry" — must
    // land next to the Revert button, not only in the page-top banner.
    importJobRevertError.value = formatPartnerDirectoryError(error, "Failed to revert this import job.");
  } finally {
    revertingJobId.value = null;
  }
}

function formatImportJobProgress(job: PartnerDirectoryImportJob): string {
  if (job.status === "queued") return "Queued";

  const parts = [`${job.recordsSucceeded}/${job.recordsTotal} imported`];
  if (job.recordsFailed > 0) parts.push(`${job.recordsFailed} failed`);
  if (job.recordsSkipped > 0) parts.push(`${job.recordsSkipped} skipped`);
  return parts.join(", ");
}

function platformAdminUrl(record: PartnerDirectoryRecord): string | null {
  return record.platformExpertId ? `/admin/expert-manager/${record.platformExpertId}` : null;
}

function platformListingUrl(record: PartnerDirectoryRecord): string | null {
  return platformListingUrlFor(record.platformExpertSlug, record);
}

function platformListingUrlFor(slug: string | null, record: PartnerDirectoryRecord | null): string | null {
  if (!slug || !PUBLIC_WEBSITE_URL) return null;
  const expertType = (record?.mappedPayload as { expert_type?: string } | null)?.expert_type ?? "agency";
  const basePath = expertType === "freelancer" ? "/freelancers" : "/agencies";
  return `${PUBLIC_WEBSITE_URL}${basePath}/${slug}`;
}

function schedulePoll() {
  if (!isPageMounted.value) return;

  clearPoll();

  pollTimer.value = window.setTimeout(
    async () => {
      await pollOperations();
    },
    shouldPollOperations.value ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS,
  );
}

async function pollOperations() {
  const taskId = activeTask.value?.id;

  try {
    if (taskId) {
      activeTask.value = await getPartnerDirectoryScrapeTask(taskId);
      await loadResultRecord();
    }

    if (selectedCrawlRunId.value && isSelectedCrawlRunActive.value) {
      await refreshSelectedCrawlRun();
    }

    await pollActiveImportJobs();

    await refreshPageData();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to poll partner directory operations.");
  } finally {
    schedulePoll();
  }
}

async function loadResultRecord() {
  const task = activeTask.value;
  const recordId = task?.resultRecordId;
  if (!task || !recordId) return;

  try {
    const { record, derivedDefaults } = await getPartnerDirectoryRecord(recordId);
    resultRecord.value = record;
    if (shouldAutoSelectResultRecord.value || !selectedRecordId.value) {
      await selectImportedRecord(record, { auto: true });
    } else if (task.status === "succeeded" && recordId === selectedRecordId.value && lastBaselineRefreshTaskId !== task.id) {
      // A re-scrape finished for the open record: re-baseline the mapper
      // exactly once per task (polling keeps the finished task around).
      lastBaselineRefreshTaskId = task.id;
      if (mappedPayloadDirty.value) {
        rescrapedBaselineStale.value = true;
      } else {
        selectedRecord.value = record;
        initializeMappedPayload(record, derivedDefaults);
      }
    }
    await refreshImportedRecords();
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load partner record:", error);
  }
}

// "Reload baseline" from the stale-baseline banner: discards the dirty draft.
async function reloadRescrapedBaseline() {
  const recordId = selectedRecordId.value;
  if (!recordId) return;
  if (mappedPayloadDirty.value && !window.confirm("Reload the scraped baseline? Your unsaved mapped-data edits will be discarded.")) return;

  try {
    const { record, derivedDefaults } = await getPartnerDirectoryRecord(recordId);
    if (selectedRecordId.value !== recordId) return;
    selectedRecord.value = record;
    initializeMappedPayload(record, derivedDefaults);
    rescrapedBaselineStale.value = false;
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to reload the scraped baseline.");
  }
}

async function selectImportedRecord(record: PartnerDirectoryRecord, options: { auto?: boolean } = {}) {
  const loadToken = selectedRecordLoadToken.value + 1;
  selectedRecordLoadToken.value = loadToken;
  selectedRecordId.value = record.id;
  selectedRecord.value = record;
  noteDraft.value = "";
  recordActionMessage.value = null;
  selectedRecordDetailFetchFailed.value = false;
  rescrapedBaselineStale.value = false;
  initializeMappedPayload(record, null);
  void loadSelectedRecordNotes();

  if (!options.auto) {
    shouldAutoSelectResultRecord.value = false;
  }

  try {
    const { record: detailedRecord, derivedDefaults } = await getPartnerDirectoryRecord(record.id);
    if (selectedRecordLoadToken.value === loadToken && selectedRecordId.value === record.id) {
      selectedRecord.value = detailedRecord;
      initializeMappedPayload(detailedRecord, derivedDefaults);
      void loadSelectedRecordNotes();
    }
  } catch (error) {
    if (selectedRecordLoadToken.value === loadToken && selectedRecordId.value === record.id) {
      // The mapper stays usable on the client prefill baseline; surface a
      // warning in its header because the comparison may be approximate.
      selectedRecordDetailFetchFailed.value = true;
    }
    console.error("[PartnerDirectoryScraperPage] Failed to load selected partner record:", error);
  }
}

// Retry the detail fetch after a failure so the mapper compares against the
// server-derived og baseline instead of the client prefill approximation.
async function retrySelectedRecordDetail() {
  const recordId = selectedRecordId.value;
  if (!recordId) return;
  if (mappedPayloadDirty.value && !window.confirm("Reloading the server baseline discards your unsaved mapped-data edits. Continue?")) return;

  try {
    const { record, derivedDefaults } = await getPartnerDirectoryRecord(recordId);
    if (selectedRecordId.value !== recordId) return;
    selectedRecord.value = record;
    initializeMappedPayload(record, derivedDefaults);
    selectedRecordDetailFetchFailed.value = false;
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to load the server baseline for this record.");
  }
}

async function selectCrawlRun(run: PartnerDirectoryCrawlRun, options: { auto?: boolean } = {}) {
  selectedCrawlRunId.value = run.id;
  selectedCrawlRun.value = run;
  crawlerMessage.value = options.auto ? crawlerMessage.value : null;
  await refreshSelectedCrawlRun();
}

async function refreshSelectedCrawlRun() {
  const runId = selectedCrawlRunId.value;
  if (!runId) return;

  try {
    const detail = await getPartnerDirectoryCrawlRun(runId);
    selectedCrawlRun.value = detail.run;
    failedCrawlPages.value = detail.failedPages;
    failedDiscoveredProfiles.value = detail.failedProfiles;
    await Promise.all([refreshCrawlPages(), refreshDiscoveredProfiles()]);
  } catch (error) {
    console.error("[PartnerDirectoryScraperPage] Failed to load crawl run:", error);
  }
}

async function refreshCrawlPages() {
  const runId = selectedCrawlRunId.value;
  if (!runId) return;

  crawlPages.value = await listPartnerDirectoryCrawlPages({
    runId,
    limit: parseVisibleListLimit(crawlPagesLimit.value),
    status: crawlPageStatusFilter.value === "all" ? undefined : crawlPageStatusFilter.value,
  });
}

async function refreshDiscoveredProfiles() {
  const runId = selectedCrawlRunId.value;
  if (!runId) return;

  discoveredProfiles.value = await listPartnerDirectoryDiscoveredProfiles({
    runId,
    limit: parseVisibleListLimit(discoveredProfilesLimit.value),
    status: discoveredProfileStatusFilter.value === "all" ? undefined : discoveredProfileStatusFilter.value,
  });
  selectedDiscoveredProfileIds.value = selectedDiscoveredProfileIds.value.filter((id) => discoveredProfiles.value.some((profile) => profile.id === id));
}

async function pauseSelectedCrawlRun() {
  await runCrawlAction(async (run) => pausePartnerDirectoryCrawlRun(run.id), "Crawler run paused.");
}

async function resumeSelectedCrawlRun() {
  await runCrawlAction(async (run) => resumePartnerDirectoryCrawlRun(run.id), "Crawler run resumed.");
  schedulePoll();
}

async function cancelSelectedCrawlRun() {
  await runCrawlAction(async (run) => cancelPartnerDirectoryCrawlRun(run.id), "Crawler run cancelled.");
}

async function resetSelectedCrawlRun() {
  const nextRun = await runCrawlAction(async (run) => resetPartnerDirectoryCrawlRun(run.id), "Crawler run reset.");
  if (nextRun) {
    await refreshCrawlRuns({ autoSelect: false });
    await selectCrawlRun(nextRun);
    schedulePoll();
  }
}

async function retrySelectedCrawlRunFailedPages() {
  const run = selectedCrawlRun.value;
  if (!run) return;

  await runCrawlAction(async () => {
    const result = await retryFailedPartnerDirectoryCrawlPages(run.id);
    crawlerMessage.value = `${result.retried} failed crawl URLs queued for retry.`;
    return (await getPartnerDirectoryCrawlRun(run.id)).run;
  }, "");
  schedulePoll();
}

async function retrySelectedCrawlRunFailedScrapes() {
  const run = selectedCrawlRun.value;
  if (!run) return;

  await runCrawlAction(async () => {
    const result = await retryFailedPartnerDirectoryScrapes(run.id);
    crawlerMessage.value =
      result.retried > 0
        ? `${result.retried} failed scrapes reset, ${result.enqueued} scrape tasks requeued.`
        : "No failed scrapes to retry.";
    if (result.enqueued > 0) {
      discoveredProfileStatusFilter.value = "scrape_queued";
      await refreshRecentTasks();
    }
    return (await getPartnerDirectoryCrawlRun(run.id)).run;
  }, "");
  schedulePoll();
}

async function enqueueSelectedCrawlRunScrapeTasks(profileIds?: string[]) {
  const run = selectedCrawlRun.value;
  if (!run) return;

  isRunningCrawlAction.value = true;
  errorMessage.value = null;
  crawlerMessage.value = null;

  try {
    const result = await enqueuePartnerDirectoryCrawlScrapeTasks({
      runId: run.id,
      force: crawlForceScrapeRefresh.value,
      limit: profileIds?.length ? null : parseOptionalPositiveInteger(manualQueueLimit.value),
      profileIds,
    });
    crawlerMessage.value = `${result.enqueued} scrape tasks queued, ${result.skipped} skipped.`;
    if (result.enqueued > 0) {
      discoveredProfileStatusFilter.value = "scrape_queued";
    }
    if (profileIds?.length) {
      selectedDiscoveredProfileIds.value = selectedDiscoveredProfileIds.value.filter((id) => !profileIds.includes(id));
    }
    await refreshSelectedCrawlRun();
    await refreshRecentTasks();
    schedulePoll();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to queue scrape tasks.");
  } finally {
    isRunningCrawlAction.value = false;
  }
}

async function enqueueOneDiscoveredProfile(profile: PartnerDirectoryDiscoveredProfile) {
  await enqueueSelectedCrawlRunScrapeTasks([profile.id]);
}

async function enqueueCheckedDiscoveredProfiles() {
  if (!selectedDiscoveredProfileIds.value.length) return;
  await enqueueSelectedCrawlRunScrapeTasks(selectedDiscoveredProfileIds.value);
}

async function updateSelectedRecordStatus(status: string) {
  const record = selectedRecord.value;
  if (!record || record.status === status || isUpdatingRecordStatus.value) return;

  isUpdatingRecordStatus.value = true;
  errorMessage.value = null;

  try {
    const updatedRecord = await updatePartnerDirectoryRecordStatus(record.id, status);
    selectedRecord.value = updatedRecord;
    selectedRecordId.value = updatedRecord.id;
    importedRecords.value = importedRecords.value.map((existingRecord) => (existingRecord.id === updatedRecord.id ? updatedRecord : existingRecord));
    await refreshImportedRecords();
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to update screening status.");
  } finally {
    isUpdatingRecordStatus.value = false;
  }
}

async function openDiscoveredProfileInIndex(profile: PartnerDirectoryDiscoveredProfile) {
  if (!profile.recordId) {
    crawlerMessage.value = profile.scrapeTaskId ? "This expert has a scrape task but no indexed record yet." : "Queue and finish scraping this expert before opening it in Index.";
    if (profile.scrapeTaskId) {
      await selectTask(profile.scrapeTaskId);
    }
    return;
  }

  try {
    const { record } = await getPartnerDirectoryRecord(profile.recordId);
    activeTab.value = "index";
    await selectImportedRecord(record);
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to open indexed expert.");
  }
}

async function runCrawlAction(action: (run: PartnerDirectoryCrawlRun) => Promise<PartnerDirectoryCrawlRun>, successMessage: string): Promise<PartnerDirectoryCrawlRun | null> {
  const run = selectedCrawlRun.value;
  if (!run) return null;

  isRunningCrawlAction.value = true;
  errorMessage.value = null;
  crawlerMessage.value = null;

  try {
    const nextRun = await action(run);
    selectedCrawlRun.value = nextRun;
    selectedCrawlRunId.value = nextRun.id;
    if (successMessage) crawlerMessage.value = successMessage;
    await refreshCrawlRuns({ autoSelect: false });
    await refreshSelectedCrawlRun();
    return nextRun;
  } catch (error) {
    errorMessage.value = formatPartnerDirectoryError(error, "Failed to update crawler run.");
    return null;
  } finally {
    isRunningCrawlAction.value = false;
  }
}

function clearPoll() {
  if (pollTimer.value !== null) {
    window.clearTimeout(pollTimer.value);
    pollTimer.value = null;
  }
}

function isScrapeTaskPending(task: PartnerDirectoryScrapeTask): boolean {
  return task.status === "queued" || task.status === "running";
}

function parseOptionalPositiveInteger(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function parseVisibleListLimit(value: string): number {
  return Math.min(parseOptionalPositiveInteger(value) ?? (apiConfig.value?.defaultListLimit ?? 25), apiMaxListLimit.value);
}

function getProfile(record: PartnerDirectoryRecord): ScrapedProfile {
  return ((record.rawPayload as ScrapedPayload).profile ?? {}) as ScrapedProfile;
}

function servicesFromRecord(record: PartnerDirectoryRecord): string[] {
  const profile = getProfile(record);
  return uniqueStrings([...toArray(record.services), ...toArray(profile.specializedServices), ...toArray(profile.otherServices)]);
}

function toArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function clampRating(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.min(5, Math.max(0, value)) : 0;
}

function ratingFromBasisText(value: string | null | undefined): number {
  const match = value?.match(/\d+(?:\.\d+)?/);
  return match ? clampRating(Number(match[0])) : 0;
}

function partnerTierFromScrapedTier(value: string | null | undefined): ShopexpertsPreviewExpert["profile"]["partner_tier"] {
  const normalized = value?.trim().toLowerCase();
  return normalized === "select" || normalized === "plus" || normalized === "premier" || normalized === "platinum" ? normalized : "none";
}

function valueToDisplayString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  if (value && typeof value === "object") {
    const objectValue = value as Record<string, unknown>;
    for (const key of ["name", "label", "title", "service", "category", "slug"]) {
      const displayValue = valueToDisplayString(objectValue[key]);
      if (displayValue) return displayValue;
    }
  }

  return null;
}

function uniqueStrings(values: unknown[]): string[] {
  return Array.from(new Set(values.map(valueToDisplayString).filter((value): value is string => Boolean(value))));
}

function formatPartnerDirectoryError(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error && error.message === "Network Error") {
    apiHealthStatus.value = "offline";
    return "Partner Directory API is unreachable. Start the partner-directory API service on port 8085, then refresh this page.";
  }

  // API error responses (e.g. 409 active-job conflicts, 422 nothing-eligible)
  // carry a human-readable message in their body.
  const responseError = (error as { response?: { data?: { error?: unknown } } } | null)?.response?.data?.error;
  if (typeof responseError === "string" && responseError) return responseError;

  return error instanceof Error ? error.message : fallbackMessage;
}

function importMissingFields(record: PartnerDirectoryRecord): string[] {
  const missingFields: string[] = [];
  const profile = getProfile(record);

  if (!record.name && !profile.name) missingFields.push("name");
  if (!record.websiteUrl && !profile.contact?.website) missingFields.push("website");
  if (!record.primaryLocation && !record.country) missingFields.push("location");
  if (!uniqueStrings([...toArray(record.services), ...toArray(profile.specializedServices), ...toArray(profile.otherServices)]).length) {
    missingFields.push("services");
  }

  return missingFields;
}

function formatImportReadiness(record: PartnerDirectoryRecord): string {
  const missingFields = importMissingFields(record);
  return missingFields.length ? `Missing: ${missingFields.join(", ")}` : "Ready to import";
}

function statusVariant(status: PartnerDirectoryScrapeTaskStatus | string): "primary" | "secondary" | "rounded" | "dark" {
  if (status === "succeeded" || status === "approved" || status === "imported") return "primary";
  if (status === "failed" || status === "rejected") return "dark";
  if (status === "running" || status === "needs_review") return "rounded";
  return "secondary";
}

function formatStatus(status: string): string {
  return status.replace(/_/g, " ");
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "Not yet";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatMoney(value: number | null | undefined): string {
  if (typeof value !== "number") return "Unknown";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value / 100);
}

function formatPrice(price: ScrapedPrice | null | undefined, fallbackAmountCents?: number | null): string {
  if (price?.label && typeof price.amountCents === "number") {
    return `${price.label} (${formatMoney(price.amountCents)})`;
  }

  if (price?.label) return price.label;
  return formatMoney(fallbackAmountCents);
}

function formatOptionalMoney(value: number | null | undefined): string | null {
  return typeof value === "number" ? formatMoney(value) : null;
}

function formatRating(record: PartnerDirectoryRecord): string {
  if (typeof record.rating !== "number") return "Unknown";
  const reviews = typeof record.reviewCount === "number" ? ` (${record.reviewCount.toLocaleString()} reviews)` : "";
  return `${record.rating.toFixed(1)}${reviews}`;
}

function formatList(values: unknown[] | null | undefined): string {
  const formattedValues = uniqueStrings(toArray(values));
  if (!formattedValues.length) return "Unknown";
  return formattedValues.join(", ");
}

function formatShortUrl(value: string | null | undefined): string {
  if (!value) return "Unknown";

  try {
    const url = new URL(value);
    return `${url.hostname}${url.pathname === "/" ? "" : url.pathname}${url.search}`;
  } catch {
    return value;
  }
}

function distributionWidth(count: number): string {
  return `${Math.max(4, Math.round((count / maxRatingDistributionCount.value) * 100))}%`;
}

watch(crawlPageStatusFilter, () => {
  void refreshCrawlPages();
});

watch(crawlPagesLimit, () => {
  void refreshCrawlPages();
});

watch(discoveredProfileStatusFilter, () => {
  void refreshDiscoveredProfiles();
});

watch(discoveredProfilesLimit, () => {
  void refreshDiscoveredProfiles();
});

watch(crawlRunsLimit, () => {
  void refreshCrawlRuns({ autoSelect: false });
});

watch(recentTasksLimit, () => {
  void refreshRecentTasks();
});

function resetAndRefreshImportedRecords() {
  importedRecordsPage.value = 1;
  selectedRecordId.value = null;
  selectedRecord.value = null;
  clearRecordSelection();
  void refreshImportedRecords();
}

watch(importedRecordsLimit, resetAndRefreshImportedRecords);

watch([recordStatusFilter, recordMinRating, recordMinReviews, recordSort], resetAndRefreshImportedRecords);

watch(recordSearch, () => {
  if (recordSearchRefreshTimer !== null) window.clearTimeout(recordSearchRefreshTimer);
  recordSearchRefreshTimer = window.setTimeout(() => {
    recordSearchRefreshTimer = null;
    resetAndRefreshImportedRecords();
  }, 250);
});

watch(activeTab, (tab) => {
  if (tab === "mapping" && !hasLoadedMappings.value) {
    void refreshMappings();
  }
});

watch(mappedSubcategoryToAdd, (value) => {
  addMappedSubcategory(value);
});

onMounted(() => {
  isPageMounted.value = true;
  void loadSubcategoryOptions();
  void refreshMappings();
  void refreshPageDataAndSchedulePoll();
});

onBeforeUnmount(() => {
  isPageMounted.value = false;
  clearPoll();
  if (recordSearchRefreshTimer !== null) window.clearTimeout(recordSearchRefreshTimer);
});
</script>

<template>
  <main class="partner-directory-editor flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
    <AdminEditorTopBar
      :sidebar-open="expertsPanelOpen"
      title="Partner Directory"
      :subtitle="activeTab === 'crawler' ? crawlerSubtitle : activeTab === 'mapping' ? 'Global value mappings for imported expert payloads' : activeTab === 'import' ? 'Bulk sync approved records into the platform backend' : `${indexedRecordsCount} indexed experts`"
      :url-value="crawlSeedUrl || DEFAULT_CRAWL_SEED_URL"
      :show-url="activeTab === 'crawler'"
      :status-label="topBarStatusLabel"
      :can-save="activeTab === 'crawler' ? canStartCrawl : true"
      :is-saving="isStartingCrawl"
      :save-label="topBarSaveLabel"
      :show-back="false"
      sidebar-label-open="Close list"
      sidebar-label-closed="Open list"
      @toggle-sidebar="expertsPanelOpen = !expertsPanelOpen"
      @submit-url="commitCrawlerSeedUrl"
      @save="runTopBarAction"
    >
      <template #status>
        <UiTag :text="apiHealthTag.text" :variant="apiHealthTag.variant" narrow />
        <UiTag :text="topBarStatusLabel" variant="secondary" narrow />
      </template>
    </AdminEditorTopBar>

    <div v-if="errorMessage" class="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2">
      <UiText as="p" variant="p-small" class="text-red-700" :text="errorMessage" />
    </div>

    <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div class="flex h-12 shrink-0 items-end gap-8 border-b border-se-gray-300 px-4">
        <button type="button" class="flex h-12 items-center border-b-2 px-1" :class="activeTab === 'crawler' ? 'border-se-dark' : 'border-transparent'" @click="activeTab = 'crawler'">
          <UiText as="span" variant="p-small" :class="activeTab === 'crawler' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" :text="`Crawler ${crawlerTabCount}`" />
        </button>
        <button type="button" class="flex h-12 items-center border-b-2 px-1" :class="activeTab === 'index' ? 'border-se-dark' : 'border-transparent'" @click="activeTab = 'index'">
          <UiText as="span" variant="p-small" :class="activeTab === 'index' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" :text="`Index ${indexedRecordsCount}`" />
        </button>
        <button type="button" class="flex h-12 items-center border-b-2 px-1" :class="activeTab === 'import' ? 'border-se-dark' : 'border-transparent'" @click="activeTab = 'import'">
          <UiText as="span" variant="p-small" :class="activeTab === 'import' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" text="Import" />
        </button>
        <button type="button" class="flex h-12 items-center border-b-2 px-1" :class="activeTab === 'mapping' ? 'border-se-dark' : 'border-transparent'" @click="activeTab = 'mapping'">
          <UiText as="span" variant="p-small" :class="activeTab === 'mapping' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" text="Mapping" />
        </button>
      </div>

      <section v-if="activeTab === 'crawler'" class="min-h-0 flex-1 overflow-hidden">
          <div class="flex h-full min-h-0 overflow-hidden border-t border-se-gray-200">
            <aside v-if="expertsPanelOpen" class="flex min-h-0 w-80 shrink-0 flex-col overflow-hidden border-r border-se-gray-300 bg-white">
              <div class="shrink-0 border-b border-se-gray-300 p-4">
                <div class="flex items-center gap-1.5">
                  <UiText as="h2" variant="h6" text="New crawl" />
                  <UiTooltip content="Walks the Shopify partner directory listing pages and collects expert profile links. Scraping full profiles is a separate step you can queue afterwards, or automatically.">
                    <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                  </UiTooltip>
                </div>
                <div class="mt-3 grid grid-cols-2 gap-2">
                  <div>
                    <div class="mb-1 flex items-center gap-1">
                      <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Max pages" />
                      <UiTooltip content="Stop after crawling this many listing pages. Blank crawls until the last page.">
                        <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                      </UiTooltip>
                    </div>
                    <UiNumberField v-model="crawlMaxPages" name="crawler-max-pages" :min="1" placeholder="All" />
                  </div>
                  <div>
                    <div class="mb-1 flex items-center gap-1">
                      <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Max profiles" />
                      <UiTooltip content="Stop once this many profiles have been discovered. Blank means no limit.">
                        <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                      </UiTooltip>
                    </div>
                    <UiNumberField v-model="crawlMaxProfiles" name="crawler-max-profiles" :min="1" placeholder="All" />
                  </div>
                </div>
                <div class="mt-3 flex flex-col gap-2">
                  <div class="flex items-center gap-1.5">
                    <UiCheckbox v-model="crawlQueueScrapeTasks" label="Auto-queue scrapes" name="crawler-auto-queue" />
                    <UiTooltip content="When the crawl finishes, automatically queue scrape tasks for the newly discovered profiles.">
                      <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                    </UiTooltip>
                  </div>
                  <div v-if="crawlQueueScrapeTasks" class="ml-7">
                    <div class="mb-1 flex items-center gap-1">
                      <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Auto-queue limit" />
                      <UiTooltip content="Queue at most this many profiles when the crawl finishes. Blank queues every eligible profile.">
                        <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                      </UiTooltip>
                    </div>
                    <UiNumberField v-model="crawlAutoQueueLimit" name="crawler-auto-queue-limit" :min="1" placeholder="All eligible" />
                  </div>
                  <div class="flex items-center gap-1.5">
                    <UiCheckbox v-model="crawlForceScrapeRefresh" label="Force re-scrape indexed" name="crawler-force-refresh" />
                    <UiTooltip content="Scrape profiles again even if they already have an indexed record. Applies to auto-queue and the manual Queue scrapes / Queue selected buttons.">
                      <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                    </UiTooltip>
                  </div>
                </div>
                <UiButton variant="primary" label="Start crawl" class="mt-3 w-full" :disabled="!canStartCrawl" @click="startCrawlRun" />
              </div>

              <div class="flex shrink-0 items-center justify-between gap-2 border-b border-se-gray-200 px-4 py-2">
                <div class="flex items-baseline gap-1.5">
                  <UiText as="h2" variant="h6" text="Runs" />
                  <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="String(crawlRuns.length)" />
                </div>
                <div class="flex items-center gap-2">
                  <div class="w-32 shrink-0">
                    <UiSelect v-model="crawlRunsLimit" name="crawler-runs-limit" :options="listLimitOptions" />
                  </div>
                  <UiTooltip content="Refresh runs">
                    <UiIconButton icon="refresh-cw" aria-label="Refresh runs" @click="refreshCrawlRuns({ autoSelect: true })" />
                  </UiTooltip>
                </div>
              </div>

              <div v-if="crawlRuns.length === 0" class="flex flex-1 items-center justify-center p-6 text-center">
                <UiText as="p" variant="p-small" class="text-se-gray-600" text="No crawler runs yet." />
              </div>

              <div v-else class="min-h-0 flex-1 overflow-y-auto p-2">
                <button
                  v-for="run in crawlRuns"
                  :key="run.id"
                  type="button"
                  class="w-full rounded-md px-3 py-2.5 text-left hover:bg-se-gray-100"
                  :class="{ 'bg-se-gray-100 ring-1 ring-inset ring-se-gray-300': selectedCrawlRunId === run.id }"
                  @click="selectCrawlRun(run)"
                >
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <UiText as="span" variant="p-small" class="block truncate text-se-dark" :text="formatShortUrl(run.seedUrl)" />
                      <UiText as="span" variant="caption-normal" class="block truncate text-se-gray-700" :text="formatDate(run.createdAt)" />
                    </div>
                    <UiTag :text="formatStatus(run.status)" :variant="statusVariant(run.status)" narrow />
                  </div>
                  <UiText
                    as="span"
                    variant="caption-normal"
                    class="mt-1 block truncate text-se-gray-700"
                    :text="`${run.pagesSucceeded}/${run.pagesDiscovered} URLs - ${run.profilesDiscovered} experts - ${run.profilesNew} new`"
                  />
                </button>
              </div>

              <div class="shrink-0 border-t border-se-gray-300 p-4">
                <div class="flex items-center gap-1.5">
                  <UiText as="h3" variant="h6" text="Scrape one expert" />
                  <UiTooltip content="Immediately scrape a single profile. Paste a full profile URL (https://www.shopify.com/partners/directory/partner/...) or just the slug.">
                    <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                  </UiTooltip>
                </div>
                <UiInputField v-model="input" name="single-profile-input" placeholder="Profile URL or slug" class="mt-2" />
                <div class="mt-2 flex items-center justify-between gap-2">
                  <div class="flex items-center gap-1.5">
                    <UiCheckbox v-model="force" label="Force re-scrape" name="single-profile-force" />
                    <UiTooltip content="Scrape again even if this expert is already indexed.">
                      <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                    </UiTooltip>
                  </div>
                  <UiButton variant="primary" size="sm" :label="isSubmitting ? 'Scraping...' : 'Scrape now'" :disabled="!canSubmit" @click="startScrapeTask" />
                </div>
                <div v-if="activeTask" class="mt-3 rounded-md border border-se-gray-200 bg-[#fbfbf8] p-2.5">
                  <div class="flex items-center justify-between gap-2">
                    <UiText as="span" variant="caption-normal" class="font-semibold text-se-gray-700" text="Active scrape" />
                    <UiTag :text="formatStatus(activeTask.status)" :variant="statusVariant(activeTask.status)" narrow />
                  </div>
                  <div class="mt-1.5 flex flex-col gap-0.5">
                    <UiText v-for="row in activeTaskMeta" :key="row.label" as="p" variant="caption-normal" class="truncate text-se-gray-600" :text="`${row.label}: ${row.value}`" />
                    <UiText v-if="activeTask.errorMessage" as="p" variant="caption-normal" class="break-words text-red-700" :text="activeTask.errorMessage" />
                  </div>
                </div>
                <div class="mt-4 border-t border-se-gray-200 pt-3">
                  <UiText as="h3" variant="h6" text="Repair review counts" />
                  <UiText
                    as="p"
                    variant="caption-normal"
                    class="mt-1 text-se-gray-600"
                    text="Re-scrape indexed profiles that have a positive review count but no captured reviews."
                  />
                  <UiButton
                    variant="outline"
                    class="mt-2 w-full"
                    :label="isRepairingReviewCounts ? 'Scheduling…' : 'Repair inconsistent reviews'"
                    :disabled="isRepairingReviewCounts"
                    @click="repairReviewCountMismatches"
                  />
                  <UiText v-if="reviewRepairMessage" as="p" variant="caption-normal" class="mt-2 text-se-gray-700" :text="reviewRepairMessage" />
                </div>
              </div>
            </aside>

            <section class="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
              <div v-if="!selectedCrawlRun" class="flex flex-1 items-center justify-center p-8 text-center">
                <UiText as="p" variant="p-small" class="text-se-gray-600" text="Select or start a crawler run." />
              </div>

              <template v-else>
                <div class="shrink-0 border-b border-se-gray-300 px-5 py-4">
                  <div class="flex flex-wrap items-start justify-between gap-4">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <UiText as="h2" variant="h4" class="break-words text-se-gray-950" :text="formatShortUrl(selectedCrawlRun.seedUrl)" />
                        <UiTag :text="formatStatus(selectedCrawlRun.status)" :variant="statusVariant(selectedCrawlRun.status)" narrow />
                      </div>
                      <UiText as="p" variant="caption-normal" class="mt-1 break-all text-se-gray-600" :text="selectedCrawlRun.seedUrl" />
                    </div>

                    <div class="flex flex-wrap items-center justify-end gap-2">
                      <UiButton v-if="selectedRunCanPause" variant="outline" label="Pause" :disabled="isRunningCrawlAction" @click="pauseSelectedCrawlRun" />
                      <UiButton v-if="selectedRunCanResume" variant="outline" label="Resume" :disabled="isRunningCrawlAction" @click="resumeSelectedCrawlRun" />
                      <UiButton v-if="selectedRunCanCancel" variant="outline" label="Cancel" :disabled="isRunningCrawlAction" @click="cancelSelectedCrawlRun" />
                      <UiButton variant="outline" label="Reset" :disabled="isRunningCrawlAction" @click="resetSelectedCrawlRun" />
                      <UiButton variant="outline" label="Retry failed URLs" :disabled="isRunningCrawlAction || !selectedRunHasFailedPages" @click="retrySelectedCrawlRunFailedPages" />
                      <UiTooltip content='Cap for "Queue scrapes". Blank queues every discovered profile.'>
                        <div class="w-32 shrink-0">
                          <UiNumberField v-model="manualQueueLimit" name="selected-run-queue-limit" :min="1" placeholder="Queue limit" />
                        </div>
                      </UiTooltip>
                      <UiTooltip :content="queueScrapesTooltip">
                        <UiButton variant="primary" label="Queue scrapes" :disabled="isRunningCrawlAction || !selectedRunHasDiscoveredProfiles" @click="enqueueSelectedCrawlRunScrapeTasks()" />
                      </UiTooltip>
                      <UiTooltip content="Resets this run's failed scrapes back to 'discovered' and queues fresh scrape tasks for them.">
                        <UiButton variant="outline" label="Retry failed scrapes" :disabled="isRunningCrawlAction || !selectedRunHasFailedScrapes" @click="retrySelectedCrawlRunFailedScrapes" />
                      </UiTooltip>
                    </div>
                  </div>

                  <div class="mt-3 flex flex-wrap gap-x-5 gap-y-1">
                    <UiText as="span" variant="caption-normal" class="text-se-gray-700" :text="`${selectedCrawlRun.pagesSucceeded}/${selectedCrawlRun.pagesDiscovered} URLs`" />
                    <UiText as="span" variant="caption-normal" class="text-se-gray-700" :text="`${selectedCrawlRun.profilesDiscovered} experts discovered`" />
                    <UiText as="span" variant="caption-normal" class="text-se-gray-700" :text="`${queueDiscoveredProfilesCount} ready`" />
                    <UiText as="span" variant="caption-normal" class="text-se-gray-700" :text="`${queueFailedProfilesCount} failed scrapes`" />
                  </div>

                  <div v-if="crawlerMessage" class="mt-3 border border-se-gray-200 bg-[#f7f7f4] px-3 py-2">
                    <UiText as="p" variant="caption-normal" class="text-se-gray-700" :text="crawlerMessage" />
                  </div>
                </div>

                <div class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_360px] overflow-hidden">
                  <section class="flex min-h-0 min-w-0 flex-col overflow-hidden border-r border-se-gray-200">
                    <div class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-se-gray-200 px-4 py-3">
                      <div>
                        <UiText as="h3" variant="h5" text="Crawled experts" />
                        <UiText as="p" variant="caption-normal" class="text-se-gray-600" :text="`${selectedCrawlRunProfilesCount} shown`" />
                      </div>
                      <div class="flex flex-wrap items-center gap-2">
                        <UiButton variant="outline" label="Queue selected" :disabled="isRunningCrawlAction || !selectedDiscoveredProfiles.length" @click="enqueueCheckedDiscoveredProfiles" />
                        <div class="w-32 shrink-0">
                          <UiSelect v-model="discoveredProfilesLimit" name="crawler-profile-row-limit" :options="listLimitOptions" />
                        </div>
                        <div class="w-44 shrink-0">
                          <UiSelect v-model="discoveredProfileStatusFilter" name="crawler-profile-status-filter" :options="discoveredProfileStatusOptions" />
                        </div>
                      </div>
                    </div>

                    <div v-if="discoveredProfiles.length" class="min-h-0 flex-1 overflow-y-auto">
                      <div class="sticky top-0 z-10 grid grid-cols-[42px_minmax(220px,1fr)_150px_160px_120px] gap-3 border-b border-se-gray-200 bg-[#f7f7f4] px-4 py-2">
                        <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="" />
                        <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Expert" />
                        <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Status" />
                        <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Task / record" />
                        <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Actions" />
                      </div>
                      <div
                        v-for="profile in discoveredProfiles"
                        :key="profile.id"
                        class="grid grid-cols-[42px_minmax(220px,1fr)_150px_160px_120px] items-center gap-3 border-b border-se-gray-200 px-4 py-3 hover:bg-[#f7f7f4]"
                      >
                        <UiCheckbox v-model="selectedDiscoveredProfileIds" :value="profile.id" :disabled="profile.status !== 'discovered'" :name="`select-profile-${profile.id}`" />
                        <div class="min-w-0">
                          <UiText as="span" variant="p-small" class="block truncate font-semibold text-se-gray-950" :text="profile.sourceSlug ?? 'Unknown slug'" />
                          <a :href="profile.sourceUrl" target="_blank" rel="noopener noreferrer" class="group/link flex items-center gap-1 text-se-gray-600 hover:text-se-dark">
                            <UiText as="span" variant="caption-normal" class="block truncate underline-offset-2 group-hover/link:underline" :text="formatShortUrl(profile.sourceUrl)" />
                            <UiIcon name="link-external-02" class="h-3 w-3 shrink-0" />
                          </a>
                        </div>
                        <UiTag :text="formatStatus(profile.status)" :variant="statusVariant(profile.status)" narrow />
                        <div class="min-w-0">
                          <UiText as="span" variant="caption-normal" class="block truncate text-se-gray-600" :text="profile.scrapeTaskId ? `Task ${profile.scrapeTaskId}` : 'No scrape task'" />
                          <UiText as="span" variant="caption-normal" class="block truncate text-se-gray-600" :text="profile.recordId ? 'Indexed record ready' : 'No indexed record'" />
                        </div>
                        <div class="flex items-center justify-end gap-1">
                          <UiTooltip content="Open partner profile on Shopify">
                            <UiIconButton icon="link-external-02" aria-label="Open partner profile on Shopify" :href="profile.sourceUrl" target="_blank" />
                          </UiTooltip>
                          <UiTooltip v-if="profile.status === 'discovered'" content="Queue a scrape for this expert">
                            <UiIconButton icon="plus-square" aria-label="Queue a scrape for this expert" :disabled="isRunningCrawlAction" @click="enqueueOneDiscoveredProfile(profile)" />
                          </UiTooltip>
                          <UiTooltip :content="profile.recordId ? 'Open indexed record in the Index tab' : 'No indexed record yet'">
                            <UiIconButton icon="preview" aria-label="Open indexed record" :disabled="!profile.recordId" @click="openDiscoveredProfileInIndex(profile)" />
                          </UiTooltip>
                        </div>
                      </div>
                    </div>

                    <div v-else class="flex flex-1 items-center justify-center p-8 text-center">
                      <UiText as="p" variant="p-small" class="text-se-gray-600" text="No crawled experts match this filter." />
                    </div>
                  </section>

                  <aside class="flex min-h-0 flex-col overflow-hidden bg-[#fbfbf8]">
                    <div class="shrink-0 border-b border-se-gray-200 px-4 py-3">
                      <UiText as="h3" variant="h5" text="Crawl URLs" />
                      <UiText as="p" variant="caption-normal" class="text-se-gray-600" :text="`${selectedCrawlRunPagesCount} shown`" />
                    </div>
                    <div class="flex shrink-0 items-center gap-2 border-b border-se-gray-200 p-3">
                      <div class="w-32 shrink-0">
                        <UiSelect v-model="crawlPagesLimit" name="crawl-url-row-limit" :options="listLimitOptions" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <UiSelect v-model="crawlPageStatusFilter" name="crawl-url-status-filter" :options="crawlPageStatusOptions" />
                      </div>
                    </div>
                    <div class="min-h-0 flex-1 overflow-y-auto p-3">
                      <div v-if="crawlPages.length" class="flex flex-col gap-2">
                        <div v-for="page in crawlPages" :key="page.id" class="border border-se-gray-200 bg-white p-3">
                          <div class="flex items-start justify-between gap-2">
                            <UiText as="span" variant="caption-normal" class="break-all font-semibold text-se-gray-950" :text="formatShortUrl(page.url)" />
                            <UiTag :text="formatStatus(page.status)" :variant="statusVariant(page.status)" narrow />
                          </div>
                          <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-600" :text="`${page.attempts} attempts - ${page.discoveredProfileCount} experts`" />
                          <UiText v-if="page.nextPageUrl" as="p" variant="caption-normal" class="mt-1 truncate text-se-gray-600" :text="`Next ${formatShortUrl(page.nextPageUrl)}`" />
                          <UiText v-if="page.errorMessage" as="p" variant="caption-normal" class="mt-1 text-red-700" :text="page.errorMessage" />
                        </div>
                      </div>
                      <UiText v-else as="p" variant="p-small" class="text-se-gray-600" text="No crawl URLs match this filter." />
                    </div>
                  </aside>
                </div>
              </template>
            </section>
          </div>
      </section>

      <section v-else-if="activeTab === 'index'" class="min-h-0 flex-1 overflow-hidden">
          <div class="flex h-full min-h-0 overflow-hidden border-t border-se-gray-200">
            <aside v-if="expertsPanelOpen" class="flex min-h-0 w-80 shrink-0 flex-col overflow-hidden border-r border-se-gray-300 bg-white" :style="indexSidebarStyle">
              <div class="border-b border-se-gray-300 px-4 py-3">
                <UiText as="h2" variant="h6" text="Experts" />
                <UiText
                  as="p"
                  variant="caption-normal"
                  class="mt-1 text-se-gray-700"
                  :text="matchingRecordsCount === 0 ? 'No matching experts' : `${importedRecordsRangeStart}–${importedRecordsRangeEnd} of ${matchingRecordsCount} matching experts`"
                />
              </div>
              <!-- Search stays inline; every other filter lives in the drawer so the list gets the height. -->
              <div class="flex flex-col gap-2 border-b border-se-gray-300 px-4 py-3">
                <div class="flex items-center gap-2">
                  <div class="min-w-0 flex-1">
                    <UiInputField v-model="recordSearch" name="partner-directory-record-search" icon="search-md" placeholder="Search name, mapped name, slug, location…" />
                  </div>
                  <UiTooltip content="Refresh experts">
                    <UiIconButton icon="refresh-cw" aria-label="Refresh experts" @click="refreshIndexedRecords" />
                  </UiTooltip>
                </div>
                <div class="flex items-center gap-2">
                  <div class="min-w-0 flex-1">
                    <PartnerDirectoryIndexFilters
                      v-model:status="recordStatusFilter"
                      v-model:countries="recordCountryFilterModel"
                      v-model:min-rating="recordMinRating"
                      v-model:min-reviews="recordMinReviews"
                      v-model:sort="recordSort"
                      v-model:page-size="importedRecordsLimit"
                      :status-options="recordStatusFilterOptions"
                      :country-options="recordCountryOptions"
                      :min-rating-options="recordMinRatingOptions"
                      :min-reviews-options="recordMinReviewsOptions"
                      :sort-options="recordSortOptions"
                      :page-size-options="listLimitOptions"
                      @reset="resetIndexFilters"
                      @select-all-countries="selectAllRecordCountries"
                      @deselect-all-countries="deselectAllRecordCountries"
                    />
                  </div>
                  <UiTooltip content="Requeue scrape tasks stuck in 'queued' or 'running' so the scraper picks them up again. Use this when queued experts stop progressing.">
                    <UiButton variant="outline" :label="isRecoveringQueue ? 'Recovering…' : 'Recover queue'" :disabled="isRecoveringQueue" @click="recoverScrapeQueue" />
                  </UiTooltip>
                </div>
                <UiText v-if="queueRecoveryMessage" as="p" variant="caption-normal" class="text-se-gray-700" :text="queueRecoveryMessage" />
              </div>
              <div class="flex shrink-0 items-center border-b border-se-gray-300 px-4 py-2">
                <UiCheckbox
                  :model-value="allPageRecordsSelected"
                  :label="selectedRecordIdCount > 0 ? `${selectedRecordIdCount} selected` : 'Select all on page'"
                  :disabled="filteredRecords.length === 0"
                  name="select-all-records-on-page"
                  @update:model-value="toggleSelectAllOnPage"
                />
              </div>
              <PartnerDirectoryBulkActionBar
                v-if="selectedRecordIdCount > 0"
                v-model:sync-target-status="bulkSyncTargetStatus"
                :count="selectedRecordIdCount"
                :busy="isRunningBulkAction"
                @rescrape="bulkRescrapeSelectedRecords"
                @approve="bulkUpdateSelectedRecordsStatus('approved')"
                @reject="bulkUpdateSelectedRecordsStatus('rejected')"
                @needs-review="bulkUpdateSelectedRecordsStatus('needs_review')"
                @sync="bulkSyncSelectedRecords"
                @clear="clearRecordSelection"
              />
              <div v-if="bulkActionMessage" class="shrink-0 border-b border-se-gray-200 bg-[#f7f7f4] px-4 py-2">
                <UiText as="p" variant="caption-normal" class="text-se-gray-700" :text="bulkActionMessage" />
              </div>
              <div v-if="filteredRecords.length === 0" class="flex flex-1 items-center justify-center p-6 text-center">
                <UiText as="p" variant="p-small" class="text-se-gray-600" text="No indexed experts match this search." />
              </div>
              <div v-else class="relative min-h-0 flex-1 overflow-hidden">
                <div class="h-full overflow-y-auto p-2" @mouseleave="hoveredRecordIndex = null">
                  <PartnerDirectoryRecordListCard
                    v-for="(record, recordIndex) in filteredRecords"
                    :key="record.id"
                    :record="record"
                    :active="selectedRecordId === record.id"
                    :checked="selectedRecordIds.has(record.id)"
                    @mouseenter="hoveredRecordIndex = recordIndex"
                    @focusin="hoveredRecordIndex = recordIndex"
                    @select="selectImportedRecord(record)"
                    @toggle-checked="toggleRecordChecked(record.id, recordIndex, $event)"
                  />
                </div>
                <div
                  v-if="hoveredRecordPosition"
                  class="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-se-dark/85 px-1.5 leading-tight"
                  data-testid="record-list-position"
                >
                  <UiText as="span" variant="caption-normal" class="whitespace-nowrap text-se-white" :text="hoveredRecordPosition" />
                </div>
              </div>
              <div v-if="matchingRecordsCount > 0" class="flex shrink-0 items-center justify-between gap-2 border-t border-se-gray-300 px-3 py-2.5">
                <UiButton
                  variant="outline"
                  size="sm"
                  label="Previous"
                  :disabled="!canGoToPreviousImportedRecordsPage"
                  @click="goToImportedRecordsPage(importedRecordsPage - 1)"
                />
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="whitespace-nowrap text-se-gray-700"
                  :text="`Page ${importedRecordsPage} of ${importedRecordsPageCount}`"
                />
                <UiButton
                  variant="outline"
                  size="sm"
                  label="Next"
                  :disabled="!canGoToNextImportedRecordsPage"
                  @click="goToImportedRecordsPage(importedRecordsPage + 1)"
                />
              </div>
            </aside>

            <!-- Drag to resize the expert list; double-click resets to the default width. -->
            <div
              v-if="expertsPanelOpen"
              class="relative z-10 -mx-[3px] w-1.5 shrink-0 cursor-col-resize touch-none select-none transition-colors"
              :class="isResizingIndexSidebar ? 'bg-se-gray-400' : 'hover:bg-se-gray-300'"
              role="separator"
              tabindex="0"
              aria-orientation="vertical"
              aria-label="Resize the expert list"
              :aria-valuemin="PARTNER_DIRECTORY_INDEX_SIDEBAR_MIN_WIDTH"
              :aria-valuemax="PARTNER_DIRECTORY_INDEX_SIDEBAR_MAX_WIDTH"
              :aria-valuenow="indexSidebarWidth ?? PARTNER_DIRECTORY_INDEX_SIDEBAR_DEFAULT_WIDTH"
              title="Drag to resize. Double-click to reset."
              @pointerdown="startIndexSidebarResize"
              @pointermove="onIndexSidebarResize"
              @pointerup="endIndexSidebarResize"
              @pointercancel="endIndexSidebarResize"
              @keydown="onIndexSidebarResizeKeydown"
              @dblclick="resetIndexSidebarWidth"
            />

            <section class="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
              <div v-if="!selectedRecord" class="flex flex-1 items-center justify-center p-8 text-center">
                <UiText as="p" variant="p-small" class="text-se-gray-600" text="Select an indexed expert to inspect scraped data." />
              </div>

              <template v-else>
                <div class="shrink-0 border-b border-se-gray-300 px-5 py-4">
                  <div class="flex flex-wrap items-start justify-between gap-4">
                    <div class="flex min-w-0 items-start gap-3">
                      <PartnerDirectoryRecordAvatar :record="selectedRecord" :alt="selectedLogoAlt" class="h-12 w-12" contain />
                      <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                          <UiText as="h2" variant="h4" class="break-words text-se-gray-950" :text="selectedRecord.name ?? selectedRecord.sourceSlug ?? 'Unknown expert'" />
                          <UiTag :text="formatStatus(selectedRecord.status)" :variant="statusVariant(selectedRecord.status)" narrow />
                          <UiTooltip v-if="selectedRecord.platformClaimed" content="This expert claimed their platform profile. Syncs only update reviews and confidence signals - profile fields stay untouched.">
                            <UiTag text="Claimed on platform" variant="primary" narrow />
                          </UiTooltip>
                          <UiTooltip content="Name, website, location, and services are required before an expert can be imported.">
                            <UiTag :text="selectedRecordImportReadiness.text" :variant="selectedRecordImportReadiness.variant" narrow />
                          </UiTooltip>
                          <UiTag v-if="selectedProfile?.partnerType" :text="selectedProfile.partnerType" variant="secondary" narrow />
                          <UiTooltip v-if="selectedRecordNoteCount > 0" content="Has screener notes">
                            <div class="flex items-center gap-0.5">
                              <UiIcon name="chat-bubble" class="h-3.5 w-3.5 text-se-gray-500" />
                              <UiText as="span" variant="caption-normal" class="text-se-gray-600" :text="String(selectedRecordNoteCount)" />
                            </div>
                          </UiTooltip>
                        </div>
                        <UiLink
                          v-if="selectedRecordSourceHref"
                          :href="selectedRecordSourceHref"
                          :label="selectedRecord.sourceUrl"
                          variant="p-small"
                          external="with-arrow"
                          overflow-ellipsis
                          class="mt-1 block max-w-full"
                        />
                        <!-- Unlinkable values stay visible as text so the admin can see what was scraped. -->
                        <UiText v-else as="p" variant="caption-normal" class="mt-1 break-all text-se-gray-600" :text="selectedRecord.sourceUrl" />
                        <UiText v-if="selectedRecord.importError" as="p" variant="caption-normal" class="mt-1 text-red-700" :text="`Import failed: ${selectedRecord.importError}`" />
                        <UiText v-if="recordActionMessage" as="p" variant="caption-normal" class="mt-1 text-se-gray-700" :text="recordActionMessage" />
                      </div>
                    </div>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                      <UiButton
                        v-for="action in screeningActions"
                        :key="action.status"
                        :variant="selectedRecord.status === action.status ? 'primary' : 'outline'"
                        :label="action.label"
                        :disabled="isUpdatingRecordStatus || selectedRecord.status === action.status"
                        @click="updateSelectedRecordStatus(action.status)"
                      />
                      <UiTooltip content="Queue a fresh scrape of this expert's Shopify profile. Progress shows up through the regular polling.">
                        <UiButton
                          variant="outline"
                          :label="isRescrapingRecord ? 'Queueing…' : 'Re-scrape'"
                          :disabled="isRescrapingRecord"
                          @click="rescrapeSelectedRecord"
                        />
                      </UiTooltip>
                      <UiButton
                        v-if="selectedRecord.websiteUrl || selectedProfile?.contact?.website"
                        variant="outline"
                        label="Website"
                        :href="selectedRecord.websiteUrl ?? selectedProfile?.contact?.website ?? undefined"
                        target="_blank"
                      />
                      <UiButton
                        v-if="selectedRecord.status === 'approved' || selectedRecord.status === 'imported'"
                        variant="outline"
                        :label="syncingRecordId === selectedRecord.id ? 'Syncing…' : selectedRecord.status === 'imported' ? 'Re-sync' : 'Sync to platform'"
                        :disabled="syncingRecordId !== null"
                        @click="syncRecordToPlatform(selectedRecord)"
                      />
                      <UiButton v-if="platformAdminUrl(selectedRecord)" variant="outline" label="Admin profile →" :href="platformAdminUrl(selectedRecord) ?? undefined" target="_blank" />
                      <UiButton v-if="platformListingUrl(selectedRecord)" variant="outline" label="View listing ↗" :href="platformListingUrl(selectedRecord) ?? undefined" target="_blank" />
                      <UiButton
                        v-if="selectedRecord.platformExpertId && selectedRecord.importExpertCreated"
                        variant="outline"
                        :label="revertingRecordId === selectedRecord.id ? 'Reverting…' : 'Revert import'"
                        :disabled="revertingRecordId !== null"
                        @click="revertRecordImport(selectedRecord)"
                      />
                    </div>
                  </div>
                </div>

                <div class="flex h-11 shrink-0 items-end gap-6 border-b border-se-gray-300 px-5">
                  <button type="button" class="h-11 border-b-2 px-1 text-left" :class="expertViewerTab === 'screener' ? 'border-se-dark' : 'border-transparent'" @click="expertViewerTab = 'screener'">
                    <UiText as="span" variant="p-small" :class="expertViewerTab === 'screener' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" text="Screener" />
                  </button>
                  <button type="button" class="h-11 border-b-2 px-1 text-left" :class="expertViewerTab === 'notes' ? 'border-se-dark' : 'border-transparent'" @click="expertViewerTab = 'notes'">
                    <UiText as="span" variant="p-small" :class="expertViewerTab === 'notes' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" :text="selectedRecordNoteCount > 0 ? `Notes (${selectedRecordNoteCount})` : 'Notes'" />
                  </button>
                  <button type="button" class="h-11 border-b-2 px-1 text-left" :class="expertViewerTab === 'preview' ? 'border-se-dark' : 'border-transparent'" @click="expertViewerTab = 'preview'">
                    <UiText as="span" variant="p-small" :class="expertViewerTab === 'preview' ? 'font-semibold text-se-dark' : 'text-se-gray-600'" text="Preview" />
                  </button>
                </div>

                <div v-if="expertViewerTab === 'screener'" class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                  <section v-if="mappedPayloadDraft" class="mb-5 border border-se-gray-300 bg-[#fbfbf8] p-4">
                    <div class="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div class="flex items-center gap-1.5">
                          <UiText as="h3" variant="h5" text="Mapped data" />
                          <UiTooltip content="The data that will be imported into shopexperts. Edit it freely - the scraped data below is never modified. Only fields that differ from the scraped values are stored as overrides.">
                            <UiIcon name="info-circle" class="h-3.5 w-3.5 text-se-gray-400" />
                          </UiTooltip>
                          <UiTag v-if="mappedOverrideFieldCount > 0" :text="mappedOverrideFieldCountLabel" variant="rounded" narrow />
                        </div>
                        <UiText
                          as="p"
                          variant="caption-normal"
                          class="mt-1 text-se-gray-600"
                          :text="mappedImportMissingFields.length ? `Missing for import: ${mappedImportMissingFields.join(', ')}` : 'Ready for import checks.'"
                        />
                        <div v-if="selectedRecordDetailFetchFailed" class="mt-1 flex flex-wrap items-center gap-2">
                          <UiText as="p" variant="caption-normal" class="text-amber-700" text="Server baseline unavailable — comparison may be approximate." />
                          <UiLinkButton label="Retry" variant="ghost" text-variant="caption-normal" hover-decoration="underline" @click="retrySelectedRecordDetail" />
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <UiTooltip content="Reset every field to its scraped value. Save afterwards to clear the stored overrides.">
                          <UiButton variant="outline" label="Clear all" :disabled="isSavingMappedPayload || mappedOverrideFieldCount === 0" @click="clearAllMappedOverrides" />
                        </UiTooltip>
                        <UiButton variant="outline" label="Reset" :disabled="isSavingMappedPayload || !mappedPayloadDirty" @click="resetMappedPayload" />
                        <UiButton variant="primary" :label="isSavingMappedPayload ? 'Saving...' : 'Save mapped data'" :disabled="isSavingMappedPayload || !mappedPayloadDirty" @click="saveMappedPayload" />
                      </div>
                    </div>

                    <div v-if="rescrapedBaselineStale" class="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                      <UiText as="p" variant="caption-normal" class="text-amber-800" text="Record was re-scraped — the scraped baseline changed. Your unsaved edits still compare against the old baseline." />
                      <UiButton variant="outline" size="sm" label="Reload baseline" @click="reloadRescrapedBaseline" />
                    </div>

                    <div class="mt-4 grid gap-4 lg:grid-cols-2">
                      <div class="lg:col-span-2">
                        <UiText as="span" variant="caption-normal" class="mb-1 block text-se-gray-500" text="Existing expert" />
                        <UiAsyncCombobox
                          v-model="mappedExpertSearchQuery"
                          v-model:selected-id="mappedExpertId"
                          v-model:selected-items="mappedExpertSelectedItems"
                          :data="mappedExpertSearchResults"
                          :is-fetching="isFetchingMappedExperts"
                          :is-error="Boolean(mappedExpertSearchError)"
                          :error="mappedExpertSearchError"
                          name="mapped-existing-expert"
                          mode="preserve"
                          hide-combobox-when-empty
                          show-selected-image
                          placeholder="Search experts by name or email"
                          hint="Optional. Link this record to an existing expert so it can be updated instead of creating a duplicate."
                        >
                          <template #leading-icon>
                            <UiIcon name="search" class="h-5 w-5 text-se-gray-500" />
                          </template>
                        </UiAsyncCombobox>
                        <UiText v-if="showNoMappedExpertMatch" as="p" variant="caption-normal" class="mt-2 text-se-gray-600" text="No experts found matching this search." />
                      </div>
                      <PartnerDirectoryMappedFieldRow
                        label="Expert type"
                        :overridden="isMappedFieldOverridden('expert_type')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.expert_type)"
                        @reset="resetMappedField('expert_type')"
                      >
                        <div class="w-44 shrink-0">
                          <UiSelect v-model="mappedPayloadDraft.expert_type" name="mapped-expert-type" :options="expertTypeOptions" />
                        </div>
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        v-if="mappedPayloadDraft.expert_type === 'agency'"
                        label="Agency name"
                        :overridden="isMappedFieldOverridden('agency_name')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.agency_name)"
                        @reset="resetMappedField('agency_name')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.agency_name ?? ''" name="mapped-agency-name" placeholder="Agency name" @update:model-value="(value) => setMappedTextField('agency_name', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <template v-else>
                        <PartnerDirectoryMappedFieldRow
                          label="First name"
                          :overridden="isMappedFieldOverridden('first_name')"
                          :scraped-value="formatScrapedValue(mappedPayloadBaseline?.first_name)"
                          @reset="resetMappedField('first_name')"
                        >
                          <UiInputField :model-value="mappedPayloadDraft.first_name ?? ''" name="mapped-first-name" placeholder="First name" @update:model-value="(value) => setMappedTextField('first_name', value)" />
                        </PartnerDirectoryMappedFieldRow>
                        <PartnerDirectoryMappedFieldRow
                          label="Last name"
                          :overridden="isMappedFieldOverridden('last_name')"
                          :scraped-value="formatScrapedValue(mappedPayloadBaseline?.last_name)"
                          @reset="resetMappedField('last_name')"
                        >
                          <UiInputField :model-value="mappedPayloadDraft.last_name ?? ''" name="mapped-last-name" placeholder="Last name" @update:model-value="(value) => setMappedTextField('last_name', value)" />
                        </PartnerDirectoryMappedFieldRow>
                      </template>
                      <PartnerDirectoryMappedFieldRow
                        class="lg:col-span-2"
                        label="Bio"
                        :overridden="isMappedFieldOverridden('bio')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.bio)"
                        @reset="resetMappedField('bio')"
                      >
                        <UiTextarea :model-value="mappedPayloadDraft.bio ?? ''" name="mapped-bio" placeholder="Expert bio" :rows="5" @update:model-value="(value) => setMappedTextField('bio', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="Country"
                        tooltip="Use ISO 3166-1 alpha-2 country codes, for example US or GB."
                        :overridden="isMappedFieldOverridden('country')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.country)"
                        @reset="resetMappedField('country')"
                      >
                        <div class="w-32 shrink-0">
                          <UiInputField :model-value="mappedPayloadDraft.country ?? ''" name="mapped-country" placeholder="US" @update:model-value="setMappedCountry" />
                        </div>
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="Starting price (USD)"
                        :overridden="isMappedFieldOverridden('starting_price')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.starting_price)"
                        @reset="resetMappedField('starting_price')"
                      >
                        <div class="w-40 shrink-0">
                          <UiNumberField v-model="mappedStartingPriceInput" name="mapped-starting-price" :min="0" placeholder="0" />
                        </div>
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="Timezone"
                        :overridden="isMappedFieldOverridden('timezone')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.timezone)"
                        @reset="resetMappedField('timezone')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.timezone ?? ''" name="mapped-timezone" placeholder="America/New_York" @update:model-value="(value) => setMappedTextField('timezone', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="Phone"
                        :overridden="isMappedFieldOverridden('phone_no')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.phone_no)"
                        @reset="resetMappedField('phone_no')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.phone_no ?? ''" name="mapped-phone" placeholder="Phone number" @update:model-value="(value) => setMappedTextField('phone_no', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        class="lg:col-span-2"
                        label="Languages"
                        :overridden="isMappedFieldOverridden('languages')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.languages)"
                        @reset="resetMappedField('languages')"
                      >
                        <UiInputField v-model="mappedLanguagesInput" name="mapped-languages" placeholder="en, fr, de" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="Website"
                        :overridden="isMappedFieldOverridden('social_links.website')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.social_links.website)"
                        @reset="resetMappedSocialLinkField('website')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.social_links.website ?? ''" name="mapped-website" placeholder="https://example.com" @update:model-value="(value) => setMappedSocialLink('website', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="Shopify directory link"
                        :overridden="isMappedFieldOverridden('social_links.shopify_directory_link')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.social_links.shopify_directory_link)"
                        @reset="resetMappedSocialLinkField('shopify_directory_link')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.social_links.shopify_directory_link ?? ''" name="mapped-shopify-link" placeholder="https://www.shopify.com/partners/..." @update:model-value="(value) => setMappedSocialLink('shopify_directory_link', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="LinkedIn URL"
                        :overridden="isMappedFieldOverridden('social_links.linkedin_url')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.social_links.linkedin_url)"
                        @reset="resetMappedSocialLinkField('linkedin_url')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.social_links.linkedin_url ?? ''" name="mapped-linkedin" placeholder="https://linkedin.com/..." @update:model-value="(value) => setMappedSocialLink('linkedin_url', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <PartnerDirectoryMappedFieldRow
                        label="X URL"
                        :overridden="isMappedFieldOverridden('social_links.x_url')"
                        :scraped-value="formatScrapedValue(mappedPayloadBaseline?.social_links.x_url)"
                        @reset="resetMappedSocialLinkField('x_url')"
                      >
                        <UiInputField :model-value="mappedPayloadDraft.social_links.x_url ?? ''" name="mapped-x" placeholder="https://x.com/..." @update:model-value="(value) => setMappedSocialLink('x_url', value)" />
                      </PartnerDirectoryMappedFieldRow>
                      <div class="lg:col-span-2">
                        <UiText as="span" variant="caption-normal" class="mb-1 block text-se-gray-500" text="Effective service subcategories" />
                        <div class="flex flex-wrap gap-2">
                          <UiTag v-for="option in globalMappedSubcategoryOptions" :key="`global-${option.value}`" :text="`${option.label} · global`" variant="primary" narrow />
                          <div v-for="option in additionalSubcategoryOptions" :key="`additional-${option.value}`" class="inline-flex items-center gap-1 bg-se-secondary pr-1">
                            <UiTag :text="option.label" variant="secondary" narrow />
                            <UiTooltip content="Remove additional subcategory">
                              <UiIconButton icon="close" :aria-label="`Remove ${option.label}`" @click="removeMappedSubcategory(option.value)" />
                            </UiTooltip>
                          </div>
                          <UiText v-if="!effectiveSubcategoryOptions.length" as="span" variant="caption-normal" class="text-se-gray-600" text="No subcategories selected." />
                        </div>
                        <UiText
                          as="p"
                          variant="caption-normal"
                          class="mt-2 text-se-gray-600"
                          text="Global service mappings update this list automatically. Use this field only for extra subcategories specific to this expert."
                        />
                        <div class="mt-2 flex items-center gap-2">
                          <div class="w-80 shrink-0">
                            <UiCombobox v-model="mappedSubcategoryToAdd" name="mapped-subcategory-add" :options="addableSubcategoryOptions" :option-groups="addableSubcategoryOptionGroups" placeholder="Add additional subcategory" open-on-click />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div v-for="fact in selectedRecordFacts" :key="fact.label" class="border border-se-gray-200 bg-[#fbfbf8] p-3">
                      <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="fact.label" />
                      <UiText as="p" variant="p-small" class="mt-1 break-words text-se-gray-950" :text="fact.value" />
                    </div>
                  </div>

                  <div v-if="selectedProfile?.about?.businessDescription" class="mt-5 border-b border-se-gray-200 pb-5">
                    <UiText as="h3" variant="h5" text="Profile summary" />
                    <UiText as="p" variant="p-small" class="mt-2 max-w-4xl text-se-gray-700" :text="selectedProfile.about.businessDescription" />
                  </div>

                  <div class="mt-5 grid gap-6 2xl:grid-cols-[minmax(0,1fr)_360px]">
                    <div class="min-w-0">
                      <section class="border-b border-se-gray-200 pb-5">
                        <div class="flex items-center justify-between gap-3">
                          <UiText as="h3" variant="h5" text="Services" />
                          <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="`${selectedRecordServices.length} total`" />
                        </div>
                        <div v-if="selectedSpecializedServices.length" class="mt-3 grid gap-3 md:grid-cols-2">
                          <div v-for="service in selectedSpecializedServices" :key="service.name" class="border border-se-gray-200 bg-[#fbfbf8] p-3">
                            <div class="flex flex-wrap items-start justify-between gap-2">
                              <UiText as="p" variant="p-small" class="font-semibold text-se-gray-950" :text="service.name" />
                              <UiTag v-if="service.priceLabel || service.priceAmountCents" :text="service.priceLabel ?? formatOptionalMoney(service.priceAmountCents) ?? 'Price captured'" variant="secondary" narrow />
                            </div>
                            <UiText v-if="service.description" as="p" variant="caption-normal" class="mt-2 text-se-gray-700" :text="service.description" />
                          </div>
                        </div>
                        <div v-if="selectedOtherServices.length" class="mt-3 flex flex-wrap gap-2">
                          <UiTag v-for="service in selectedOtherServices" :key="service" :text="service" variant="secondary" narrow />
                        </div>
                        <UiText v-if="!hasSelectedServices" as="p" variant="p-small" class="mt-3 text-se-gray-600" text="No services were captured." />
                      </section>

                      <section class="border-b border-se-gray-200 py-5">
                        <UiText as="h3" variant="h5" text="Locations, languages, and industries" />
                        <div class="mt-3 grid gap-4 md:grid-cols-3">
                          <div>
                            <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Supported locations" />
                            <UiText as="p" variant="p-small" class="mt-1 text-se-gray-800" :text="formatList(selectedSupportedLocations)" />
                          </div>
                          <div>
                            <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Languages" />
                            <UiText as="p" variant="p-small" class="mt-1 text-se-gray-800" :text="formatList(selectedLanguages)" />
                          </div>
                          <div>
                            <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Industries" />
                            <UiText as="p" variant="p-small" class="mt-1 text-se-gray-800" :text="formatList(selectedIndustries)" />
                          </div>
                        </div>
                      </section>

                      <section class="border-b border-se-gray-200 py-5">
                        <div class="flex items-center justify-between gap-3">
                          <UiText as="h3" variant="h5" text="Captured reviews" />
                          <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="`${selectedRecordReviews.length} captured${selectedRecord.reviewCount ? ` of ${selectedRecord.reviewCount.toLocaleString()}` : ''}`" />
                        </div>
                        <div v-if="selectedRecordReviews.length" class="mt-3 divide-y divide-se-gray-200">
                          <div v-for="(review, index) in selectedRecordReviews" :key="`${review.author}-${review.date}-${index}`" class="py-3 first:pt-0 last:pb-0">
                            <div class="flex flex-wrap items-center justify-between gap-2">
                              <UiText as="span" variant="p-small" class="font-semibold text-se-gray-950" :text="review.author ?? 'Unknown reviewer'" />
                              <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="`${review.rating ?? 'No'} rating - ${review.date ?? 'Unknown date'}`" />
                            </div>
                            <UiText as="p" variant="p-small" class="mt-2 text-se-gray-700" :text="review.text ?? 'No review text captured.'" />
                          </div>
                        </div>
                        <UiText v-else as="p" variant="p-small" class="mt-3 text-se-gray-600" text="No reviews were captured." />
                      </section>

                      <details class="mt-5 border border-se-gray-200 bg-[#101211]">
                        <summary class="cursor-pointer list-none px-4 py-3">
                          <UiText as="span" variant="p-small" class="font-semibold text-white" text="Raw scrape payload" />
                        </summary>
                        <pre class="max-h-[420px] overflow-auto border-t border-white/10 p-4 text-xs leading-relaxed text-white">{{ selectedRecordJson }}</pre>
                      </details>
                    </div>

                    <aside class="min-w-0">
                      <section class="border-b border-se-gray-200 pb-5">
                        <UiText as="h3" variant="h5" text="Rating distribution" />
                        <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-500" :text="selectedProfile?.ratingSummary?.summaryText ?? 'Overall rating summary'" />
                        <div v-if="ratingDistribution.length" class="mt-4 flex flex-col gap-2">
                          <div v-for="item in ratingDistribution" :key="item.stars" class="grid grid-cols-[34px_minmax(0,1fr)_44px] items-center gap-2">
                            <UiText as="span" variant="caption-normal" class="text-se-gray-700" :text="`${item.stars}`" />
                            <div class="h-2 overflow-hidden bg-se-gray-200">
                              <div class="h-full bg-se-dark" :style="{ width: distributionWidth(item.count) }" />
                            </div>
                            <UiText as="span" variant="caption-normal" class="text-right text-se-gray-700" :text="item.count.toLocaleString()" />
                          </div>
                        </div>
                        <UiText v-else as="p" variant="p-small" class="mt-3 text-se-gray-600" text="No rating distribution was captured." />
                      </section>

                      <section class="border-b border-se-gray-200 py-5">
                        <UiText as="h3" variant="h5" text="Contact" />
                        <div class="mt-3 flex flex-col gap-2">
                          <UiText as="p" variant="caption-normal" class="break-all text-se-gray-700" :text="formatShortUrl(selectedRecord.websiteUrl ?? selectedProfile?.contact?.website)" />
                          <UiText as="p" variant="caption-normal" class="break-all text-se-gray-700" :text="selectedProfile?.contact?.email ?? 'No email captured'" />
                          <UiText as="p" variant="caption-normal" class="break-all text-se-gray-700" :text="selectedProfile?.contact?.phone ?? 'No phone captured'" />
                          <UiText as="p" variant="caption-normal" class="text-se-gray-700" :text="selectedRecord.primaryLocation ?? 'No primary location'" />
                        </div>
                      </section>

                      <section class="border-b border-se-gray-200 py-5">
                        <UiText as="h3" variant="h5" text="Social links" />
                        <div v-if="selectedSocialLinks.length" class="mt-3 flex flex-col gap-3">
                          <div v-for="link in selectedSocialLinks" :key="link.label" class="min-w-0">
                            <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="link.label" />
                            <UiText as="p" variant="caption-normal" class="mt-1 break-all text-se-gray-800" :text="formatShortUrl(link.url)" />
                          </div>
                        </div>
                        <UiText v-else as="p" variant="p-small" class="mt-3 text-se-gray-600" text="No social links were captured." />
                      </section>

                      <section class="py-5">
                        <UiText as="h3" variant="h5" text="Page metadata" />
                        <div class="mt-3 flex flex-col gap-3">
                          <div v-for="fact in selectedPageFacts" :key="fact.label" class="min-w-0">
                            <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="fact.label" />
                            <UiText as="p" variant="caption-normal" class="mt-1 break-words text-se-gray-800" :text="fact.value" />
                          </div>
                        </div>
                      </section>
                    </aside>
                  </div>
                </div>
                <div v-else-if="expertViewerTab === 'notes'" class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                  <section class="border border-se-gray-300 bg-[#f7f7f4] p-4">
                    <UiText as="h3" variant="h5" text="Screener notes" />
                    <UiTextarea v-model="noteDraft" name="record-note-draft" class="mt-3" placeholder="Leave a note for other screeners..." :rows="4" />
                    <div class="mt-3 flex justify-end">
                      <UiButton variant="primary" :label="isSavingNote ? 'Adding...' : 'Add note'" :disabled="isSavingNote || !noteDraft.trim()" @click="addRecordNote" />
                    </div>
                  </section>

                  <div class="mt-4 flex flex-col gap-3">
                    <UiText v-if="isLoadingNotes" as="p" variant="p-small" class="text-se-gray-600" text="Loading notes..." />
                    <UiText v-else-if="notes.length === 0" as="p" variant="p-small" class="text-se-gray-600" text="No notes yet." />
                    <article v-for="note in notes" v-else :key="note.id" class="border border-se-gray-200 bg-[#fbfbf8] p-3">
                      <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0">
                          <UiText as="span" variant="caption-normal" class="block truncate font-semibold text-se-gray-800" :text="note.authorAdminEmail ?? 'Unknown admin'" />
                          <UiText as="span" variant="caption-normal" class="block text-se-gray-500" :text="formatDate(note.createdAt)" />
                        </div>
                        <UiTooltip content="Delete note">
                          <UiIconButton icon="delete" aria-label="Delete note" :disabled="deletingNoteIds.includes(note.id)" @click="deleteRecordNote(note)" />
                        </UiTooltip>
                      </div>
                      <UiText as="p" variant="p-small" class="mt-3 whitespace-pre-line text-se-gray-800" :text="note.body" />
                    </article>
                  </div>
                </div>
                <div v-else-if="expertViewerTab === 'preview'" class="min-h-0 flex-1 overflow-hidden">
                  <PartnerDirectoryRecordPreview v-if="previewBody && previewCrawlerProfile" :body="previewBody" :reviews="previewReviews" :crawler-profile="previewCrawlerProfile" />
                  <div v-else class="px-6 py-5">
                    <UiText as="p" variant="p-small" class="text-se-gray-600" text="Preview unavailable — mapped data has not loaded for this record yet." />
                  </div>
                </div>
              </template>
            </section>
          </div>
      </section>

      <section v-else-if="activeTab === 'import'" class="min-h-0 flex-1 overflow-y-auto border-t border-se-gray-200 bg-white p-6">
        <div class="mx-auto flex max-w-3xl flex-col gap-4">
          <section class="border border-se-gray-300 bg-[#f7f7f4] p-4">
            <div class="flex items-start justify-between gap-4">
              <div>
                <UiText as="h2" variant="h6" text="Import approved records" />
                <UiText as="p" variant="p-small" class="mt-1 text-se-gray-600" text="Syncs every approved record into the platform backend as an Expert: profile data, social links, avatar, regions, services, and reviews. Records missing a usable name are skipped and stay approved for the next run." />
              </div>
              <div class="flex shrink-0 items-end gap-2">
                <div>
                  <UiText as="label" variant="caption-normal" class="mb-1 block text-se-gray-600" text="Import as" />
                  <UiSelect v-model="newImportJobTargetStatus" name="import-job-target-status" :options="importJobTargetStatusOptions" :disabled="isStartingImportJob" />
                </div>
                <UiButton
                  variant="primary"
                  :label="isStartingImportJob ? 'Starting…' : activeImportJobs.length ? 'Queue import job' : 'Start import job'"
                  :disabled="isStartingImportJob"
                  @click="startImportJob"
                />
              </div>
            </div>
          </section>

          <section class="border border-se-gray-300 bg-white">
            <div class="border-b border-se-gray-200 p-4">
              <UiText as="h3" variant="h6" text="Job history" />
              <UiText v-if="importJobRevertMessage" as="p" variant="caption-normal" class="mt-1 text-se-gray-700" :text="importJobRevertMessage" />
              <UiText v-if="importJobRevertError" as="p" variant="caption-normal" class="mt-1 text-red-700" :text="importJobRevertError" />
            </div>
            <div v-if="!importJobs.length" class="p-6 text-center">
              <UiText as="p" variant="p-small" class="text-se-gray-600" text="No import jobs yet." />
            </div>
            <div v-else class="divide-y divide-se-gray-200">
              <div
                v-for="job in importJobs"
                :key="job.id"
                class="flex items-center justify-between gap-4 p-4"
                :class="selectedImportJobId === job.id ? 'bg-[#fbfbf8]' : ''"
              >
                <button type="button" class="flex flex-1 items-center gap-3 text-left hover:opacity-80" @click="selectImportJob(job)">
                  <UiTag :text="formatStatus(job.status)" :variant="statusVariant(job.status)" narrow />
                  <UiTag v-if="job.revertedAt" text="Reverted" variant="dark" narrow />
                  <div>
                    <UiText as="p" variant="p-small" weight="semibold" :text="formatImportJobProgress(job)" />
                    <UiText as="p" variant="p-small" class="text-se-gray-600" :text="`${formatDate(job.createdAt)} · Importing as ${job.targetProfileStatus}`" />
                    <UiText v-if="job.revertSummary" as="p" variant="caption-normal" class="mt-0.5 text-se-gray-600" :text="formatImportJobRevertSummary(job.revertSummary)" />
                    <UiText v-if="job.errorMessage" as="p" variant="p-small" class="mt-0.5 text-red-700" :text="job.errorMessage" />
                  </div>
                </button>
                <UiButton
                  v-if="isImportJobActive(job)"
                  variant="outline"
                  :label="isCancellingImportJob ? 'Cancelling…' : 'Cancel'"
                  :disabled="isCancellingImportJob"
                  @click="cancelImportJob(job.id)"
                />
                <UiTooltip v-if="canRevertImportJob(job)" content="Deletes experts this job created and restores fields it changed, but only where the platform still holds the imported value. Claimed profiles are never touched.">
                  <UiButton
                    variant="outline"
                    :label="revertingJobId === job.id ? 'Reverting…' : 'Revert job'"
                    :disabled="revertingJobId !== null"
                    @click="revertImportJob(job)"
                  />
                </UiTooltip>
              </div>
            </div>
            <div v-if="importJobsTotal > 0" class="flex items-center justify-between gap-2 border-t border-se-gray-300 px-4 py-2.5">
              <UiButton variant="outline" size="sm" label="Previous" :disabled="!canGoToPreviousImportJobsPage" @click="goToImportJobsPage(importJobsPage - 1)" />
              <UiText as="span" variant="caption-normal" class="whitespace-nowrap text-se-gray-700" :text="`Page ${importJobsPage} of ${importJobsPageCount}`" />
              <UiButton variant="outline" size="sm" label="Next" :disabled="!canGoToNextImportJobsPage" @click="goToImportJobsPage(importJobsPage + 1)" />
            </div>
          </section>

          <section v-if="selectedImportJobId" class="border border-se-gray-300 bg-white">
            <div class="flex items-center justify-between gap-4 border-b border-se-gray-200 p-4">
              <UiText as="h3" variant="h6" text="Job records" />
              <div class="w-52 shrink-0">
                <UiSelect
                  :model-value="selectedImportJobRecordsOutcome"
                  name="import-job-records-outcome"
                  :options="selectedImportJobOutcomeOptions"
                  @update:model-value="setImportJobRecordsOutcome"
                />
              </div>
            </div>
            <div v-if="isLoadingImportJobRecords && !selectedImportJobRecords.length" class="p-6 text-center">
              <UiText as="p" variant="p-small" class="text-se-gray-600" text="Loading…" />
            </div>
            <div v-else-if="!selectedImportJobRecords.length" class="p-6 text-center">
              <UiText
                as="p"
                variant="p-small"
                class="text-se-gray-600"
                :text="selectedImportJobRecordsOutcome === 'all' ? 'This job hasn\'t touched any records yet.' : `No ${selectedImportJobRecordsOutcome} records in this job.`"
              />
            </div>
            <div v-else class="divide-y divide-se-gray-200">
              <div v-for="row in selectedImportJobRecords" :key="row.id" class="flex items-start justify-between gap-4 p-4">
                <div class="flex min-w-0 items-start gap-3">
                  <PartnerDirectoryRecordAvatar v-if="row.record" :record="row.record" :alt="row.name ?? row.record.name ?? ''" class="h-8 w-8" />
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <UiText as="p" variant="p-small" weight="semibold" :text="row.name ?? row.record?.name ?? 'Unknown expert'" />
                      <UiTag :text="formatStatus(row.outcome)" :variant="statusVariant(row.outcome)" narrow />
                      <UiTooltip v-if="importJobRecordResolvedSince(row)" content="A later sync imported this record successfully.">
                        <UiTag text="Imported since" variant="primary" narrow />
                      </UiTooltip>
                    </div>
                    <UiText v-if="row.errorMessage" as="p" variant="p-small" class="mt-1 text-red-700" :text="row.errorMessage" />
                  </div>
                </div>
                <div class="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <UiButton v-if="row.outcome !== 'imported' && row.record" variant="outline" label="Open record" @click="openImportJobRecordInIndex(row.record)" />
                  <UiButton
                    v-if="canRetryImportJobRecord(row) && row.record"
                    variant="outline"
                    :label="syncingRecordId === row.recordId ? 'Syncing…' : 'Retry sync'"
                    :disabled="syncingRecordId !== null"
                    @click="syncRecordToPlatform(row.record, { targetProfileStatus: selectedImportJob?.targetProfileStatus })"
                  />
                  <UiButton v-if="row.platformExpertId" variant="outline" label="Admin profile →" :href="`/admin/expert-manager/${row.platformExpertId}`" target="_blank" />
                  <UiButton
                    v-if="platformListingUrlFor(row.platformExpertSlug, row.record)"
                    variant="outline"
                    label="View listing ↗"
                    :href="platformListingUrlFor(row.platformExpertSlug, row.record) ?? undefined"
                    target="_blank"
                  />
                  <UiButton
                    v-if="row.record && row.record.platformExpertId && row.record.importExpertCreated"
                    variant="outline"
                    :label="revertingRecordId === row.recordId ? 'Reverting…' : 'Revert import'"
                    :disabled="revertingRecordId !== null"
                    @click="revertRecordImport(row.record)"
                  />
                </div>
              </div>
            </div>
            <div v-if="selectedImportJobRecordsTotal > 0" class="flex items-center justify-between gap-2 border-t border-se-gray-300 px-4 py-2.5">
              <UiButton variant="outline" size="sm" label="Previous" :disabled="!canGoToPreviousImportJobRecordsPage" @click="goToImportJobRecordsPage(selectedImportJobRecordsPage - 1)" />
              <UiText as="span" variant="caption-normal" class="whitespace-nowrap text-se-gray-700" :text="`Page ${selectedImportJobRecordsPage} of ${selectedImportJobRecordsPageCount}`" />
              <UiButton variant="outline" size="sm" label="Next" :disabled="!canGoToNextImportJobRecordsPage" @click="goToImportJobRecordsPage(selectedImportJobRecordsPage + 1)" />
            </div>
          </section>
        </div>
      </section>

      <section v-else class="min-h-0 flex-1 overflow-y-auto border-t border-se-gray-200 bg-white p-6">
        <div class="mx-auto flex max-w-6xl flex-col gap-4">
          <section class="border border-se-gray-300 bg-[#f7f7f4] p-4">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <UiText as="h2" variant="h4" text="Global mapping" />
                <UiText
                  as="p"
                  variant="p-small"
                  class="mt-1 max-w-2xl text-se-gray-700"
                  text="Map Shopify partner directory service values to Shopexperts subcategories. These mappings update each expert's effective subcategories dynamically."
                />
              </div>
              <div class="flex items-center gap-2">
                <div class="w-56 shrink-0">
                  <UiSelect model-value="service" name="mapping-kind" :options="mappingKindOptions" disabled />
                </div>
                <UiTooltip content="Refresh mappings">
                  <UiIconButton icon="refresh-cw" aria-label="Refresh mappings" :disabled="isLoadingMappings" @click="refreshMappings" />
                </UiTooltip>
              </div>
            </div>
            <div class="mt-4 flex flex-wrap items-center gap-2">
              <div class="min-w-64 flex-1">
                <UiInputField v-model="mappingSearch" name="mapping-search" icon="search-md" placeholder="Search scraped service values" />
              </div>
              <div class="w-44 shrink-0">
                <UiSelect v-model="mappingFilter" name="mapping-filter" :options="mappingFilterOptions" />
              </div>
            </div>
          </section>

          <section class="overflow-hidden border border-se-gray-300 bg-white">
            <div class="grid grid-cols-[minmax(220px,1fr)_320px_120px_48px] gap-3 border-b border-se-gray-200 bg-[#f7f7f4] px-4 py-2">
              <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Source service" />
              <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Shopexperts subcategory" />
              <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Status" />
              <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="" />
            </div>

            <div v-if="isLoadingMappings" class="p-6 text-center">
              <UiText as="p" variant="p-small" class="text-se-gray-600" text="Loading mappings..." />
            </div>
            <div v-else-if="!mappingRows.length && !staleMappingRows.length" class="p-6 text-center">
              <UiText as="p" variant="p-small" class="text-se-gray-600" text="No service values match this filter." />
            </div>
            <template v-else>
              <div
                v-for="row in mappingRows"
                :key="row.sourceValue"
                class="grid grid-cols-[minmax(220px,1fr)_320px_120px_48px] items-center gap-3 border-b border-se-gray-200 px-4 py-3 hover:bg-[#fbfbf8]"
              >
                <div class="min-w-0">
                  <UiText as="span" variant="p-small" class="block truncate font-semibold text-se-gray-950" :text="row.sourceValue" />
                  <UiText as="span" variant="caption-normal" class="text-se-gray-600" :text="`${row.recordCount} records`" />
                </div>
                <div class="w-72 shrink-0">
                  <UiCombobox
                    :model-value="row.mapping?.targetValue ?? ''"
                    :name="`mapping-target-${row.sourceValue}`"
                    :options="subcategoryOptions"
                    :option-groups="subcategoryOptionGroups"
                    placeholder="Not mapped"
                    open-on-click
                    @update:model-value="(value) => updateServiceMapping(row.sourceValue, selectValue(value))"
                  />
                </div>
                <UiTag :text="row.mapping?.targetValue ? 'Mapped' : 'Unmapped'" :variant="row.mapping?.targetValue ? 'primary' : 'secondary'" narrow />
                <UiTooltip v-if="row.mapping?.targetValue && row.mapping" content="Remove mapping">
                  <UiIconButton icon="delete" :aria-label="`Remove mapping for ${row.sourceValue}`" @click="clearServiceMapping(row.mapping)" />
                </UiTooltip>
              </div>

              <div v-if="staleMappingRows.length" class="border-t border-se-gray-300 bg-[#fbfbf8] px-4 py-2">
                <UiText as="p" variant="caption-normal" class="text-se-gray-600" text="Stale mappings below do not match current scraped service data." />
              </div>
              <div
                v-for="row in staleMappingRows"
                :key="`stale-${row.mapping.id}`"
                class="grid grid-cols-[minmax(220px,1fr)_320px_120px_48px] items-center gap-3 border-b border-se-gray-200 bg-[#fbfbf8] px-4 py-3"
              >
                <div class="min-w-0">
                  <UiText as="span" variant="p-small" class="block truncate font-semibold text-se-gray-950" :text="row.sourceValue" />
                  <UiText as="span" variant="caption-normal" class="text-se-gray-600" text="No matching records" />
                </div>
                <div class="w-72 shrink-0">
                  <UiCombobox
                    :model-value="row.mapping.targetValue ?? ''"
                    :name="`stale-mapping-target-${row.mapping.id}`"
                    :options="subcategoryOptions"
                    :option-groups="subcategoryOptionGroups"
                    placeholder="Not mapped"
                    open-on-click
                    @update:model-value="(value) => updateServiceMapping(row.sourceValue, selectValue(value))"
                  />
                </div>
                <UiTag :text="row.mapping.targetValue ? 'Mapped' : 'Unmapped'" :variant="row.mapping.targetValue ? 'primary' : 'secondary'" narrow />
                <UiTooltip content="Remove mapping">
                  <UiIconButton icon="delete" :aria-label="`Remove mapping for ${row.sourceValue}`" @click="clearServiceMapping(row.mapping)" />
                </UiTooltip>
              </div>
            </template>
          </section>
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped>
.partner-directory-editor {
  height: calc(100vh - 72px);
}
</style>
